import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Transition } from "@headlessui/react";
import { PhoneIcon } from "@heroicons/react/20/solid";
import {
  ArrowLeftIcon,
  BoltIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  EyeIcon,
  EyeSlashIcon,
  PaperClipIcon,
  TrashIcon,
  UserIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { CloudArrowUpIcon } from "@heroicons/react/24/solid";
import {
  CaretSortIcon,
  CheckIcon,
  DragHandleDots2Icon,
  OpenInNewWindowIcon,
  StopwatchIcon,
} from "@radix-ui/react-icons";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Typography from "@tiptap/extension-typography";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { ObjectID } from "bson";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetFormTemplates } from "src/api/Forms/forms";
import { useGetRoles } from "src/api/Misc/Settings/roles/roles";
import {
  MeetingVendor,
  PriorityData,
  TaskTemplateData,
  TaskTemplateFileData,
  TaskTemplateUpdateRequest,
  relativeDueDateUnits,
  useCreateSubTaskTemplate,
  useDeleteTaskTemplate,
  useDeleteTaskTemplateConfirmation,
  useDeleteTaskTemplateFile,
  useDuplicateTaskTemplate,
  useGetSubTaskTemplates,
  useGetTaskTemplate,
  useUpdateTaskTemplate,
  useUploadTaskTemplateFile,
} from "src/api/Services/Journeys/journeyTemplates";
import {
  useGetJourneyTemplate,
  useGetStageAndTaskTemplatesList,
} from "src/api/Services/Tasks/journeys";
import {
  useCreateKeyMomentCondition,
  useGetKeyMoments,
} from "src/api/key-moments/key-moments";
import { Title as ReusableTitle } from "src/components/Services/Reusable/Tasks/Elements/Title";
import InputHeading from "src/components/ui/Headings/InputHeading";
import { Button } from "src/components/ui/button";
import { Checkbox } from "src/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/components/ui/command";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import {
  AlertIcon,
  NoPriorityIcon,
  SignalHigh,
  SignalLow,
  SignalMedium,
} from "src/components/ui/icons/priority-icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import {
  Select as NewSelect,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Switch } from "src/components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/ui/tabs";
import { TextInput } from "src/components/ui/text-input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { estimateToMinutes } from "src/pages/Services/Tasks/Tables/TasksTable";
import { cn } from "src/utils/ui/ui";
import TiptapEditor from "../../../../../../components/ui/editor/tiptap-editor";
import {
  Action,
  SubtaskDurationSelect,
  SubtaskRoleCombobox,
  SubtaskVisibilityButtons,
  SubtasksPolledVisibility,
  estimateOptions,
  taskTemplateLaunchConditions,
} from "../NewTaskTemplate/NewTaskTemplateModal";
import { TaskTemplateLaunchCondition } from "../NewTaskTemplate/TaskTemplateContextProvider";
import { MilestoneIcon, getSubscriberAvatar } from "../Stages/TemplateStages";
import { EditTaskTemplateContextProvider } from "./EditTaskTemplateContextProvider";
import { useTaskTemplatePanelContext } from "./TaskTemplatePanelContextProvider";

export const EditTaskTemplate = ({
  taskTemplateId,
}: {
  taskTemplateId: string;
}) => {
  const { taskTemplate, isLoading } = useGetTaskTemplate(
    new ObjectID(taskTemplateId)
  );
  const updateTaskTemplateMutation = useUpdateTaskTemplate();
  const [addSubtasks, setAddSubtasks] = useState(false);
  const [showFormTemplate, setShowFormTemplate] = useState(false);
  const taskTemplatePanelCtx = useTaskTemplatePanelContext();
  const [openRemoveFormConfirmation, setOpenRemoveFormConfirmation] =
    useState(false);
  const [showMeeting, setShowMeeting] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [isSubscribersActive, setIsSubscribersActive] = useState(
    !!taskTemplate?.subscribers?.length
  );

  useEffect(() => {
    setIsSubscribersActive(!!taskTemplate?.subscribers?.length);
  }, [isLoading]);

  return (
    <>
      {taskTemplate && (
        <EditTaskTemplateContextProvider taskTemplate={taskTemplate}>
          <Transition
            appear
            show
            className={"flex-1"}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
          >
            <div className="h-full">
              {!!taskTemplate && (
                <>
                  <div className="flex h-full flex-col bg-gray-100">
                    <div className="bg-white p-6">
                      <div className="space-y-6">
                        {taskTemplate.parentTaskTemplate && (
                          <Button
                            variant={"secondary"}
                            className="space-x-2"
                            onClick={() => {
                              if (taskTemplate.parentTaskTemplate?._id)
                                taskTemplatePanelCtx.dispatch({
                                  type: "TASK_TEMPLATE_PANEL_UPDATE",
                                  taskTemplatePanel: {
                                    id: taskTemplate.parentTaskTemplate?._id.toString(),
                                    isOpen: true,
                                  },
                                });
                            }}
                          >
                            <ArrowLeftIcon className="h-4 w-4 text-gray-400" />
                            <div>
                              <span className="font-light">
                                Go to parent task&nbsp;
                              </span>
                              <span className="font-semibold">
                                {taskTemplate.parentTaskTemplate.title}
                              </span>
                            </div>
                          </Button>
                          // <div className="inline-flex cursor-pointer rounded-md border bg-gray-50 px-2 py-0.5 hover:bg-gray-100">
                          //   <ArrowLeftIcon className="inline-block h-4 w-4 text-gray-400" />
                          //   <span>Go to parent task</span>
                          //   <span className="font-bold"></span>
                          // </div>
                        )}
                        <div className="flex justify-between">
                          <div className="w-full space-y-4">
                            <Title taskTemplate={taskTemplate} />
                          </div>
                        </div>
                      </div>
                      {/* <ParentTask taskTemplate={taskTemplate} /> */}

                      <div className="flex items-start justify-between gap-6 pt-4">
                        <div className="space-y-2">
                          <div className="flex flex-wrap gap-2">
                            {!taskTemplate.parentTaskTemplate && (
                              <StageCombobox taskTemplate={taskTemplate} />
                            )}
                            <RoleCombobox taskTemplate={taskTemplate} />
                            <Priority taskTemplate={taskTemplate} />
                            {isSubscribersActive && (
                              <SubscribersCombobox
                                taskTemplate={taskTemplate}
                              />
                            )}
                            {taskTemplate.subTaskTemplateCount ? (
                              <>
                                <SubtasksDurationSum
                                  taskTemplate={taskTemplate}
                                />
                                <SubtasksPolledVisibility
                                  isVisible={taskTemplate.isVisible}
                                />
                              </>
                            ) : (
                              <>
                                <DurationSelect taskTemplate={taskTemplate} />
                                <Visibility taskTemplate={taskTemplate} />
                              </>
                            )}
                          </div>
                          {!taskTemplate.parentTaskTemplate ? (
                            <DueDateDependency taskTemplate={taskTemplate} />
                          ) : (
                            <div className="inline-flex h-8 items-center rounded-md border border-gray-300 bg-gray-100 px-2 text-sm">
                              <span className="text-gray-500">{`Due `}</span>
                              &nbsp;
                              <span className="font-medium">{`${
                                taskTemplate.relativeDueDate.value
                              } ${taskTemplate.relativeDueDate.unit}${
                                taskTemplate.relativeDueDate.value === 1
                                  ? ""
                                  : "s"
                              }`}</span>
                              &nbsp;
                              <span className="text-gray-500">{`after`}</span>
                              &nbsp;
                              {taskTemplate.dependentOn.type === "default" && (
                                <span className="font-medium">{`project start`}</span>
                              )}
                              {taskTemplate.dependentOn.type === "task" && (
                                <>
                                  <span className="text-gray-500">{`task`}</span>
                                  &nbsp;
                                  <span className="font-medium">
                                    {taskTemplate.dependentOn.name}
                                  </span>
                                </>
                              )}
                              {taskTemplate.dependentOn.type === "stage" && (
                                <>
                                  <span className="text-gray-500">{`stage`}</span>
                                  &nbsp;
                                  <span className="font-medium">
                                    {taskTemplate.dependentOn.name}
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                          {taskTemplate.isMilestone && (
                            <Milestone taskTemplate={taskTemplate} />
                          )}
                        </div>
                        <div className="inline-flex items-center space-x-2">
                          <Options
                            taskTemplate={taskTemplate}
                            isSubscribersActive={isSubscribersActive}
                            setIsSubscribersActive={setIsSubscribersActive}
                          />
                        </div>
                      </div>

                      {!!taskTemplate.files &&
                        taskTemplate.files.length > 0 && (
                          <Attachments taskTemplate={taskTemplate} />
                        )}
                      <div
                        className={
                          "mt-4 rounded-md border bg-gray-50 px-3 py-2"
                        }
                      >
                        {!!taskTemplate._id && (
                          <div key={taskTemplate._id.toString()}>
                            <Description taskTemplate={taskTemplate} />
                          </div>
                        )}
                        {/* <Description
                          isEditing={true}
                          description={taskTemplate.description}
                          onBlur={(description) => {
                            if (
                              taskTemplate.description.trim() !==
                              description.trim()
                            ) {
                              updateTaskTemplateMutation.mutate({
                                journeyTemplateId:
                                  taskTemplate.journeyTemplate._id,
                                journeyStageTemplateId:
                                  taskTemplate.journeyStageTemplate._id,
                                params: {
                                  _id: taskTemplate._id,
                                  description,
                                },
                                parentTaskTemplateId:
                                  taskTemplate.parentTaskTemplate?._id,
                              });
                            }
                          }}
                        /> */}
                      </div>
                    </div>
                    <div className="min-h-[50vh]">
                      <Tabs
                        defaultValue={"extensions"}
                        className="flex flex-col overflow-auto"
                      >
                        <TabsList className="mb-0 shrink-0 border-gray-300 bg-white pt-6">
                          <TabsTrigger value="extensions" className="ml-6">
                            Extensions
                          </TabsTrigger>
                          {/* <TabsTrigger value="key-moments" className="ml-6">
                            Key Moment
                          </TabsTrigger> */}
                        </TabsList>
                        <div className="flex-1 overflow-auto">
                          <TabsContent
                            value="extensions"
                            className="h-full bg-gray-100 px-6"
                          >
                            {taskTemplate.action === "default" &&
                              !addSubtasks &&
                              !showFormTemplate &&
                              !taskTemplate.subTaskTemplateCount && (
                                <div className="flex gap-3 py-6">
                                  {!taskTemplate.parentTaskTemplate && (
                                    <SubtasksAction
                                      // taskTemplate={taskTemplate}
                                      setAddSubtasks={setAddSubtasks}
                                      // addSubtasks={addSubtasks}
                                      // showFormTemplate={showFormTemplate}
                                    />
                                  )}
                                  <FormAction
                                    taskTemplate={taskTemplate}
                                    setShowFormTemplate={setShowFormTemplate}
                                    showFormTemplate={showFormTemplate}
                                    addSubtasks={addSubtasks}
                                  />
                                  <MeetingAction
                                    setShowMeeting={setShowMeeting}
                                  />
                                  <FileUploadAction
                                    setShowFileUpload={setShowFileUpload}
                                    taskTemplate={taskTemplate}
                                  />
                                </div>
                              )}
                            {taskTemplate.action === "meeting" && (
                              <div className="h-full flex-1 space-y-2 pt-6">
                                <Meeting taskTemplate={taskTemplate} />
                              </div>
                            )}
                            {taskTemplate.action === "fileUpload" && (
                              <div className="h-full flex-1 space-y-2 pt-6">
                                <FileUpload taskTemplate={taskTemplate} />
                              </div>
                            )}
                            <AddMeetingDialog
                              taskTemplate={taskTemplate}
                              showMeeting={showMeeting}
                              setShowMeeting={setShowMeeting}
                            />
                            {(taskTemplate.action === "form" ||
                              showFormTemplate) && (
                              <div className="h-full flex-1 space-y-2 pt-6">
                                <div className="relative flex justify-between rounded-md border bg-white p-4">
                                  <div className="space-y-2">
                                    <InputHeading heading="Form" />
                                    <FormTemplateCombobox
                                      taskTemplate={taskTemplate}
                                    />
                                  </div>
                                  <div>
                                    <Button
                                      variant={"ghost"}
                                      className="absolute top-2 right-2 p-1.5 px-2"
                                      onClick={() => {
                                        if (taskTemplate.action !== "form") {
                                          setShowFormTemplate(false);
                                        } else {
                                          setOpenRemoveFormConfirmation(true);
                                        }
                                      }}
                                    >
                                      <TrashIcon className="h-4 w-4 text-gray-400" />{" "}
                                    </Button>
                                    <Dialog
                                      open={openRemoveFormConfirmation}
                                      onOpenChange={(open) => {
                                        setOpenRemoveFormConfirmation(open);
                                      }}
                                    >
                                      <DialogContent
                                      // onOpenAutoFocus={(e) => {
                                      //   e.preventDefault();
                                      // }}
                                      >
                                        <DialogHeader>
                                          <DialogTitle>Remove Form</DialogTitle>
                                          {/* <DialogDescription>Delete all subtasks</DialogDescription> */}
                                        </DialogHeader>
                                        <div className="grid gap-4 py-2">
                                          <p>
                                            Are you sure you want to remove the
                                            form?
                                          </p>
                                        </div>
                                        <DialogFooter>
                                          <Button
                                            onClick={() => {
                                              setOpenRemoveFormConfirmation(
                                                false
                                              );
                                            }}
                                            variant={"secondary"}
                                          >
                                            Cancel
                                          </Button>
                                          <Button
                                            onClick={() => {
                                              // remove form template
                                              updateTaskTemplateMutation.mutate(
                                                {
                                                  journeyTemplateId:
                                                    taskTemplate.journeyTemplate
                                                      ._id,
                                                  journeyStageTemplateId:
                                                    taskTemplate
                                                      .journeyStageTemplate._id,
                                                  params: {
                                                    _id: taskTemplate._id,
                                                    action: "default",
                                                  },
                                                }
                                              );
                                              setOpenRemoveFormConfirmation(
                                                false
                                              );
                                            }}
                                            variant={"destructive"}
                                          >
                                            Remove
                                          </Button>
                                        </DialogFooter>
                                      </DialogContent>
                                    </Dialog>
                                  </div>
                                </div>
                              </div>
                            )}

                            {taskTemplate.action === "default" &&
                              (addSubtasks ||
                                taskTemplate.subTaskTemplateCount) && (
                                <div className="pt-6">
                                  <Subtasks
                                    taskTemplate={taskTemplate}
                                    addSubtasks={addSubtasks}
                                    setAddSubtasks={setAddSubtasks}
                                  />
                                </div>
                              )}
                            {/* {!taskTemplate.subTaskTemplateCount &&
                              !addSubtasks &&
                              !taskTemplate.parentTaskTemplate && (
                                
                              )}
                            <>
                              {!addSubtasks &&
                                !taskTemplate.subTaskTemplateCount &&
                                !taskTemplate.parentTaskTemplate && (
                                  <SubtasksButton
                                    taskTemplate={taskTemplate}
                                    setAddSubtasks={setAddSubtasks}
                                    addSubtasks={addSubtasks}
                                  />
                                )}
                              {(addSubtasks ||
                                taskTemplate.subTaskTemplateCount) && (
                                <>
                                  
                                </>
                              )}
                            </> */}
                          </TabsContent>
                          {/* <TabsContent
                            value="key-moments"
                            className="h-full bg-gray-100 p-6"
                          >
                            <KeyMoments taskTemplate={taskTemplate} />
                          </TabsContent> */}
                        </div>
                        {/* <TabsContent
                          value="subtasks"
                          className="h-full overflow-auto bg-gray-100 px-6 pt-8"
                        >
                          
                        </TabsContent> */}
                      </Tabs>
                    </div>
                  </div>
                </>
              )}
            </div>
          </Transition>
        </EditTaskTemplateContextProvider>
      )}
    </>
  );
};

function Priority({ taskTemplate }: { taskTemplate: TaskTemplateData }) {
  const updateTaskTemplateMutation = useUpdateTaskTemplate();
  return (
    <NewSelect
      value={taskTemplate.priority || "no-priority"}
      onValueChange={(value) => {
        let priority: PriorityData | null = value as PriorityData;
        if (value === "no-priority") {
          priority = null;
        }
        updateTaskTemplateMutation.mutate({
          journeyTemplateId: taskTemplate.journeyTemplate._id,
          journeyStageTemplateId: taskTemplate.journeyStageTemplate._id,
          params: {
            _id: taskTemplate._id,
            priority,
          },
        });
      }}
    >
      <SelectTrigger className="w-32">
        {taskTemplate.priority ? (
          <SelectValue />
        ) : (
          <div className="text-gray-400">Priority</div>
        )}
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="no-priority">
          <div className="flex items-center space-x-1.5">
            <NoPriorityIcon size={16} color="gray" />
            <div>No Priority</div>
          </div>
        </SelectItem>
        <SelectItem value="low">
          <div className="flex items-center space-x-1.5">
            <SignalLow size={16} color="gray" />
            <div>Low</div>
          </div>
        </SelectItem>
        <SelectItem value="medium">
          <div className="flex items-center space-x-1.5">
            <SignalMedium size={16} color="gray" />
            <div>Medium</div>
          </div>
        </SelectItem>
        <SelectItem value="high">
          <div className="flex items-center space-x-1.5">
            <SignalHigh size={16} color="gray" />
            <div>High</div>
          </div>
        </SelectItem>
        <SelectItem value="urgent">
          <div className="flex items-center space-x-1.5">
            <AlertIcon size={16} color="gray" />
            <div>Urgent</div>
          </div>
        </SelectItem>
      </SelectContent>
    </NewSelect>
  );
}

function KeyMoments({ taskTemplate }: { taskTemplate: TaskTemplateData }) {
  const { data: keyMoments } = useGetKeyMoments();
  const [open, setOpen] = useState(false);
  const [keyMoment, setKeyMoment] = useState<string | undefined>(undefined);
  const [statusTo, setStatusTo] = useState<string>("completed");
  const createKeyMomentConditionMutation = useCreateKeyMomentCondition();
  console.log(taskTemplate);
  useEffect(() => {
    if (createKeyMomentConditionMutation.isSuccess) {
      setOpen(false);
    }
  }, [createKeyMomentConditionMutation.isSuccess]);
  return (
    <>
      <div className="space-y-4 rounded-md border bg-white p-6">
        <div className="space-y-2">
          <div className="font-medium">Key Moment</div>
          {/* <div></div> */}
        </div>
        <div>
          <Button variant={"secondary"} onClick={() => setOpen(true)}>
            Link Key Moment
          </Button>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Link Key Moment</DialogTitle>
            <DialogDescription>
              Select the key moment and define the trigger for when it should be
              registered
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-1">
              <InputHeading heading="Key Moment" required />
              <Select value={keyMoment} onValueChange={setKeyMoment}>
                <SelectTrigger className="h-9 w-72">
                  {keyMoment ? (
                    <SelectValue />
                  ) : (
                    <div className="text-gray-300">Select a key moment</div>
                  )}
                </SelectTrigger>
                <SelectContent>
                  {keyMoments?.map((keyMoment) => (
                    <SelectItem key={keyMoment._id} value={keyMoment._id}>
                      {keyMoment.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <InputHeading heading="Trigger" />
              <div className="flex gap-2">
                <Select value={"status-change-to"}>
                  <SelectTrigger className="h-9 w-fit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="status-change-to">
                      Task status changes to
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusTo} onValueChange={setStatusTo}>
                  <SelectTrigger className="h-9 w-60">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="completed">
                      <div className="flex items-center">
                        <div className="flex items-center space-x-1">
                          <div
                            className={clsx(
                              "flex h-4 w-4 items-center justify-center rounded-full bg-green-500 text-white transition-all"
                            )}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={3}
                              stroke="currentColor"
                              className="h-3 w-3"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="m4.5 12.75 6 6 9-13.5"
                              />
                            </svg>
                          </div>
                        </div>
                        <span className="ml-2 font-medium">Completed</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setOpen(false)} variant={"secondary"}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (keyMoment && taskTemplate._id) {
                  createKeyMomentConditionMutation.mutate({
                    keyMomentId: keyMoment,
                    condition: {
                      event: "task status changed",
                      filters: [
                        {
                          field: "newStatus",
                          comparator: "eq",
                          value: "Completed",
                        },
                        {
                          field: "taskTemplateId",
                          comparator: "eq",
                          value: taskTemplate._id.toString(),
                        },
                      ],
                    },
                  });
                }
              }}
              disabled={createKeyMomentConditionMutation.isLoading}
              variant={"default"}
            >
              Create
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Milestone({ taskTemplate }: { taskTemplate: TaskTemplateData }) {
  const [isDeletingMilestone, setIsDeletingMilestone] = useState(false);
  const [isDeletingMilestoneFromAllTasks, setIsDeletingMilestoneFromAllTasks] =
    useState(false);
  const updateTaskTemplateMutation = useUpdateTaskTemplate();
  const [optionsOpen, setOptionsOpen] = useState(false);
  return (
    <>
      <div className="flex h-8 w-fit items-center space-x-1 overflow-hidden rounded-md border border-input px-2.5 pr-1 shadow-sm">
        <div className="flex items-center">
          <MilestoneIcon />
          <div className="ml-2 mr-1.5 text-sm">Milestone</div>
          <DropdownMenu
            open={optionsOpen}
            onOpenChange={(isOpen) => {
              setOptionsOpen(isOpen);
            }}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant={"ghost"}
                className={clsx(
                  "h-[22px] w-[22px] rounded p-0",
                  optionsOpen ? "bg-gray-200/60" : "bg-transparent"
                )}
              >
                <EllipsisHorizontalIcon className="h-3.5 w-3.5 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" sideOffset={3}>
              <DropdownMenuItem
                className="focus:bg-red-50 focus:text-red-600"
                onClick={(e) => {
                  setIsDeletingMilestone(true);
                  e.stopPropagation();
                }}
              >
                <TrashIcon className="h-3.5 w-3.5 shrink-0" />
                <span className="ml-2 text-sm">Remove Milestone</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <Dialog
        open={isDeletingMilestone}
        onOpenChange={(open) => {
          setIsDeletingMilestone(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Milestone</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-8">
              {/* <div className="space-y-2">
                <div className="flex items-center justify-between gap-8">
                  <div className="space-y-1">
                    <InputHeading heading="Link to a Workspace Milestone" />
                    <div className="text-sm font-light text-gray-500">
                      Linking a milestone to a workspace milestone will enable
                      richer insights at the workspace level.
                    </div>
                  </div>
                  <Switch
                    checked={milestoneOptions.isLinkingWorkspaceMilestone}
                    onCheckedChange={(checked) => {
                      setMilestoneOptions({
                        ...milestoneOptions,
                        isLinkingWorkspaceMilestone: !!checked,
                      });
                    }}
                  />
                </div>
                {milestoneOptions.isLinkingWorkspaceMilestone && (
                  <ReusableMilestoneCombobox
                    milestone={milestone}
                    milestones={milestones ?? []}
                    setMilestone={(milestone) => {
                      setMilestone(milestone);
                    }}
                    usedMilestones={usedMilestones}
                  />
                )}
              </div> */}
              <div className="flex items-center justify-between gap-8">
                <div className="space-y-1">
                  <InputHeading heading="Delete from Previously Created Tasks" />
                  <div className="text-sm font-light text-gray-500">
                    Delete the milestone from tasks previously created from this
                    task template.
                  </div>
                </div>
                <Switch
                  checked={isDeletingMilestoneFromAllTasks}
                  onCheckedChange={(checked) => {
                    setIsDeletingMilestoneFromAllTasks(!!checked);
                  }}
                />
              </div>
            </div>
            {/* <div className="space-y-1">
              <InputHeading heading="Apply Retroactively" />
              <div className="flex space-x-2">
                <div className="pt-0.5">
                  <Checkbox
                    checked={retroactivelyApplyMilestone}
                    onCheckedChange={(checked) => {
                      setRetroactivelyApplyMilestone(!!checked);
                    }}
                  />
                </div>
                <div className="text-xs font-light text-gray-600">
                  Apply milestone to tasks previously created from this task
                  template in currently active projects
                </div>
              </div>
            </div> */}
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              variant={"secondary"}
              onClick={() => {
                setIsDeletingMilestone(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant={"destructive"}
              disabled={updateTaskTemplateMutation.isLoading}
              onClick={() => {
                if (taskTemplate) {
                  // if (usedMilestones?.includes(milestone?._id)) {
                  //   snackbarCtx.showSnackbar("Milestone already used", "error");
                  //   return;
                  // }
                  updateTaskTemplateMutation.mutate({
                    journeyTemplateId: taskTemplate.journeyTemplate._id,
                    journeyStageTemplateId:
                      taskTemplate.journeyStageTemplate._id,
                    params: {
                      _id: taskTemplate._id,
                      milestone: {
                        isMilestone: false,
                        applyToAllTasks: isDeletingMilestoneFromAllTasks,
                      },
                    },
                  });
                }
              }}
            >
              {updateTaskTemplateMutation.isLoading
                ? "Deleting Milestone..."
                : "Delete Milestone"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// function Milestone({ taskTemplate }: { taskTemplate: TaskTemplateData }) {
//   const [isAddingLinkedMilestone, setIsAddingLinkedMilestone] = useState(false);
//   const [milestoneOpen, setMilestoneOpen] = useState(false);
//   const [isEditingMilestone, setIsEditingMilestone] = useState(false);
//   const [milestone, setMilestone] = useState<{
//     _id: string;
//     name: string;
//   } | null>(
//     taskTemplate.milestone?._id
//       ? {
//           _id: taskTemplate.milestone._id.toString(),
//           name: taskTemplate.milestone.name,
//         }
//       : null
//   );
//   const { data: milestones } = useGetMilestones();
//   const { journeyTemplateId } = useParams();
//   const { data: taskTemplates } = useGetTaskTemplates(
//     new ObjectID(journeyTemplateId)
//   );
//   const [retroactivelyApplyMilestone, setRetroactivelyApplyMilestone] =
//     useState(false);
//   const usedMilestones = useMemo(() => {
//     return (
//       taskTemplates
//         ?.filter((t) => t.milestone?._id)
//         .map((t) => t.milestone?._id.toString() as string) ?? []
//     );
//   }, [taskTemplates]);
//   const updateTaskTemplateMutation = useUpdateTaskTemplate();
//   const snackbarCtx = useSnackBar();
//   const [isLinkedWorkspaceMilestone, setIsLinkedWorkspaceMilestone] = useState(
//     !!taskTemplate.milestone?._id
//   );
//   const [isDeletingMilestone, setIsDeletingMilestone] = useState(false);
//   const [applyDeleteToAllTasks, setApplyDeleteToAllTasks] = useState(false);
//   useEffect(() => {
//     setIsLinkedWorkspaceMilestone(!!taskTemplate.milestone?._id);
//     setMilestone(
//       taskTemplate.milestone?._id
//         ? {
//             _id: taskTemplate.milestone._id.toString(),
//             name: taskTemplate.milestone.name,
//           }
//         : null
//     );
//   }, [taskTemplate.milestone?._id]);

//   const [isUnlinkingMilestone, setIsUnlinkingMilestone] = useState(false);
//   const [applyUnlinkToAllTasks, setApplyUnlinkToAllTasks] = useState(false);

//   useEffect(() => {
//     if (updateTaskTemplateMutation.isSuccess) {
//       setIsDeletingMilestone(false);
//       setIsUnlinkingMilestone(false);
//       setIsChangingMilestone(false);
//       // setIsEditingMilestone(false);
//     }
//   }, [updateTaskTemplateMutation.isSuccess]);

//   const [newMilestone, setNewMilestone] = useState<{
//     _id: string;
//     name: string;
//   } | null>(null);
//   const [applyNewMilestoneToAllTasks, setApplyNewMilestoneToAllTasks] =
//     useState(false);
//   const [isChangingMilestone, setIsChangingMilestone] = useState(false);
//   return (
//     <>
//       <div className="flex h-8 w-fit items-center space-x-1 overflow-hidden rounded-md border border-input px-2.5 pr-1.5 shadow-sm">
//         <div className="flex items-center space-x-1.5 ">
//           <MilestoneIcon />
//           <div className="text-sm">
//             Milestone
//             {taskTemplate.milestone?.name && (
//               <span className="text-sm font-medium">{` [${taskTemplate.milestone.name}]`}</span>
//             )}
//           </div>
//         </div>
//         <Button
//           variant={"ghost"}
//           className="h-[22px] w-[22px] rounded-md p-0"
//           onClick={() => {
//             setIsEditingMilestone(true);
//           }}
//         >
//           <Pencil2Icon className="h-3.5 w-3.5 shrink-0" />
//         </Button>
//       </div>
//       <Dialog
//         open={isEditingMilestone}
//         onOpenChange={(open) => {
//           setIsEditingMilestone(open);
//         }}
//       >
//         <DialogContent>
//           <DialogHeader>
//             <DialogTitle>Edit Milestone</DialogTitle>
//           </DialogHeader>
//           <div className="space-y-8 pt-4">
//             <div className="space-y-2">
//               <div className="flex items-center justify-between gap-8">
//                 <div className="space-y-1">
//                   <InputHeading heading="Link to a Workspace Milestone" />
//                   <div className="text-sm font-light text-gray-500">
//                     Linking a milestone to a workspace milestone will enable
//                     richer insights at the workspace level.
//                   </div>
//                 </div>
//                 <Switch
//                   checked={isLinkedWorkspaceMilestone}
//                   onCheckedChange={(checked) => {
//                     if (!!checked) {
//                       setIsLinkedWorkspaceMilestone(!!checked);
//                     } else {
//                       setIsUnlinkingMilestone(true);
//                     }
//                   }}
//                 />
//               </div>
//               {isLinkedWorkspaceMilestone && (
//                 <ReusableMilestoneCombobox
//                   milestone={milestone}
//                   milestones={milestones ?? []}
//                   setMilestone={(milestone) => {
//                     setNewMilestone(milestone);
//                     setIsChangingMilestone(true);
//                   }}
//                   usedMilestones={usedMilestones}
//                 />
//               )}
//             </div>
//             <Button
//               variant={"secondary"}
//               onClick={() => {
//                 setIsDeletingMilestone(true);
//               }}
//             >
//               Delete Milestone
//             </Button>
//           </div>
//         </DialogContent>
//       </Dialog>
//       <Dialog
//         open={isChangingMilestone}
//         onOpenChange={(open) => {
//           setIsChangingMilestone(open);
//         }}
//       >
//         <DialogContent>
//           <DialogHeader>
//             <DialogTitle>Update Workspace Milestone</DialogTitle>
//           </DialogHeader>
//           <div className="space-y-6 py-4">
//             <div className="flex items-center justify-between gap-8">
//               <div className="space-y-1">
//                 <InputHeading heading="Apply Update to Previously Created Tasks" />
//                 <div className="text-sm font-light text-gray-500">
//                   Apply this workspace milestone update to tasks previously
//                   created from this task template.
//                 </div>
//               </div>
//               <Switch
//                 checked={applyNewMilestoneToAllTasks}
//                 onCheckedChange={(checked) => {
//                   setApplyNewMilestoneToAllTasks(!!checked);
//                 }}
//               />
//             </div>
//           </div>
//           <div className="flex justify-end space-x-2">
//             <Button
//               variant={"secondary"}
//               onClick={() => {
//                 setIsChangingMilestone(false);
//               }}
//             >
//               Cancel
//             </Button>
//             <Button
//               disabled={updateTaskTemplateMutation.isLoading}
//               onClick={() => {
//                 if (newMilestone?._id) {
//                   updateTaskTemplateMutation.mutate({
//                     journeyTemplateId: taskTemplate.journeyTemplate._id,
//                     journeyStageTemplateId:
//                       taskTemplate.journeyStageTemplate._id,
//                     params: {
//                       _id: taskTemplate._id,
//                       milestone: {
//                         _id: new ObjectID(newMilestone._id),
//                         applyToAllTasks: applyNewMilestoneToAllTasks,
//                       },
//                     },
//                   });
//                 }
//               }}
//             >
//               {updateTaskTemplateMutation.isLoading ? "Updating..." : "Update"}
//             </Button>
//           </div>
//         </DialogContent>
//       </Dialog>
//       <Dialog
//         open={isUnlinkingMilestone}
//         onOpenChange={(open) => {
//           setIsUnlinkingMilestone(open);
//         }}
//       >
//         <DialogContent>
//           <DialogHeader>
//             <DialogTitle>Unlink Workspace Milestone</DialogTitle>
//           </DialogHeader>
//           <div className="space-y-6 py-4">
//             <div className="flex items-center justify-between gap-8">
//               <div className="space-y-1">
//                 <InputHeading heading="Unlink from Previously Created Tasks" />
//                 <div className="text-sm font-light text-gray-500">
//                   Unlink this workspace milestone from tasks previously created
//                   from this task template.
//                 </div>
//               </div>
//               <Switch
//                 checked={applyUnlinkToAllTasks}
//                 onCheckedChange={(checked) => {
//                   setApplyUnlinkToAllTasks(!!checked);
//                 }}
//               />
//             </div>
//           </div>
//           <div className="flex justify-end space-x-2">
//             <Button
//               variant={"secondary"}
//               onClick={() => {
//                 setIsUnlinkingMilestone(false);
//               }}
//             >
//               Cancel
//             </Button>
//             <Button
//               variant={"destructive"}
//               onClick={() => {
//                 // setIsUnlinkingMilestone(false);
//                 updateTaskTemplateMutation.mutate({
//                   journeyTemplateId: taskTemplate.journeyTemplate._id,
//                   journeyStageTemplateId: taskTemplate.journeyStageTemplate._id,
//                   params: {
//                     _id: taskTemplate._id,
//                     milestone: {
//                       _id: null,
//                       applyToAllTasks: applyUnlinkToAllTasks,
//                     },
//                   },
//                 });
//               }}
//             >
//               {updateTaskTemplateMutation.isLoading ? "Unlinking..." : "Unlink"}
//             </Button>
//           </div>
//         </DialogContent>
//       </Dialog>
//       <Dialog
//         open={isDeletingMilestone}
//         onOpenChange={(open) => {
//           setIsDeletingMilestone(open);
//         }}
//       >
//         <DialogContent>
//           <DialogHeader>
//             <DialogTitle>Delete Milestone</DialogTitle>
//           </DialogHeader>
//           <div className="space-y-6 py-4">
//             <div className="flex items-center justify-between gap-8">
//               <div className="space-y-1">
//                 <InputHeading heading="Delete from Previously Created Tasks" />
//                 <div className="text-sm font-light text-gray-500">
//                   Delete this milestone from tasks previously created from this
//                   task template.
//                 </div>
//               </div>
//               <Switch
//                 checked={applyDeleteToAllTasks}
//                 onCheckedChange={(checked) => {
//                   setApplyDeleteToAllTasks(!!checked);
//                 }}
//               />
//             </div>
//           </div>
//           <div className="flex justify-end space-x-2">
//             <Button
//               variant={"secondary"}
//               onClick={() => {
//                 setIsDeletingMilestone(false);
//               }}
//             >
//               Cancel
//             </Button>
//             <Button
//               variant={"destructive"}
//               disabled={updateTaskTemplateMutation.isLoading}
//               onClick={() => {
//                 setIsDeletingMilestone(false);
//                 updateTaskTemplateMutation.mutate({
//                   journeyTemplateId: taskTemplate.journeyTemplate._id,
//                   journeyStageTemplateId: taskTemplate.journeyStageTemplate._id,
//                   params: {
//                     _id: taskTemplate._id,
//                     isMilestone: false,
//                     milestone: {
//                       applyToAllTasks: applyDeleteToAllTasks,
//                     },
//                   },
//                 });
//               }}
//             >
//               {updateTaskTemplateMutation.isLoading ? "Deleting..." : "Delete"}
//             </Button>
//           </div>
//         </DialogContent>
//       </Dialog>
//       <Dialog
//         open={isAddingLinkedMilestone}
//         onOpenChange={(open) => {
//           setIsAddingLinkedMilestone(open);
//         }}
//       >
//         <DialogContent>
//           <DialogHeader>
//             <DialogTitle>Workspace Milestone</DialogTitle>
//             <DialogDescription>
//               Linking a milestone to a workspace milestone will enable richer
//               insights at the workspace level. Click{" "}
//               <span
//                 onClick={() => {
//                   window.open(
//                     "https://docs.getbase.com/docs/user/workspace/milestones/milestones-overview",
//                     "_blank"
//                   );
//                 }}
//                 className="cursor-pointer underline hover:text-gray-600"
//               >
//                 here
//               </span>{" "}
//               to learn more.
//             </DialogDescription>
//           </DialogHeader>
//           <div className="space-y-6 py-4">
//             <div className="space-y-1">
//               <InputHeading heading="Workspace Milestone" required />
//             </div>
//             <div className="space-y-1">
//               <InputHeading heading="Apply Retroactively" />
//               <div className="flex space-x-2">
//                 <div className="pt-0.5">
//                   <Checkbox
//                     checked={retroactivelyApplyMilestone}
//                     onCheckedChange={(checked) => {
//                       setRetroactivelyApplyMilestone(!!checked);
//                     }}
//                   />
//                 </div>
//                 <div className="text-xs font-light text-gray-600">
//                   Apply milestone to tasks previously created from this task
//                   template in currently active projects
//                 </div>
//               </div>
//             </div>
//           </div>
//           <div className="flex justify-end space-x-2">
//             <Button
//               variant={"secondary"}
//               onClick={() => {
//                 setIsAddingLinkedMilestone(false);
//               }}
//             >
//               Cancel
//             </Button>
//             <Button
//               disabled={!milestone || updateTaskTemplateMutation.isLoading}
//               onClick={() => {
//                 if (milestone && taskTemplate) {
//                   if (usedMilestones?.includes(milestone._id)) {
//                     snackbarCtx.showSnackbar("Milestone already used", "error");
//                     return;
//                   }
//                   updateTaskTemplateMutation.mutate({
//                     journeyTemplateId: taskTemplate.journeyTemplate._id,
//                     journeyStageTemplateId:
//                       taskTemplate.journeyStageTemplate._id,
//                     params: {
//                       _id: taskTemplate._id,
//                       milestone: {
//                         _id: new ObjectID(milestone._id),
//                         applyToAllTasks: retroactivelyApplyMilestone,
//                       },
//                     },
//                   });
//                 }
//               }}
//             >
//               {updateTaskTemplateMutation.isLoading ? "Adding..." : "Add"}
//             </Button>
//           </div>
//         </DialogContent>
//       </Dialog>
//     </>
//   );
// }

// function ReusableMilestoneCombobox({
//   milestone,
//   setMilestone,
//   milestones,
//   usedMilestones,
// }: {
//   milestone: {
//     _id: string;
//     name: string;
//   } | null;
//   setMilestone: (
//     milestone: {
//       _id: string;
//       name: string;
//     } | null
//   ) => void;
//   milestones: MilestoneData[];
//   usedMilestones: string[];
// }) {
//   const [milestoneOpen, setMilestoneOpen] = useState(false);
//   return (
//     <Popover open={milestoneOpen} onOpenChange={setMilestoneOpen}>
//       <PopoverTrigger asChild>
//         <Button
//           variant="secondary"
//           role="combobox"
//           aria-expanded={milestoneOpen}
//           className="w-72 justify-between space-x-2 truncate"
//         >
//           <div className="flex items-center space-x-2">
//             <MilestoneIcon />
//             {!!milestone ? (
//               <div className="truncate">{milestone.name}</div>
//             ) : (
//               <div className="flex space-x-1 font-light text-gray-500">
//                 Select a workspace milestone
//               </div>
//             )}
//           </div>
//           <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
//         </Button>
//       </PopoverTrigger>
//       <PopoverContent
//         className="pointer-events-auto w-fit min-w-[320px] max-w-[700px] p-0"
//         align="start"
//       >
//         <Command>
//           <CommandInput
//             placeholder="Search workspace milestone..."
//             className="h-9"
//           />
//           <CommandEmpty>No workspace milestone found.</CommandEmpty>
//           <CommandGroup heading="Available workspace milestones">
//             {milestones
//               ?.filter(
//                 (m) =>
//                   !usedMilestones?.includes(m._id) || milestone?._id === m._id
//               )
//               .map((m) => (
//                 <CommandItem
//                   key={m._id}
//                   value={m.name}
//                   onSelect={() => {
//                     setMilestone(m);
//                     setMilestoneOpen(false);
//                   }}
//                   className={clsx("gap-2")}
//                 >
//                   <span className="truncate">{m.name}</span>
//                   <CheckIcon
//                     className={cn(
//                       "ml-auto h-4 w-4 shrink-0",
//                       m._id.toString() === milestone?._id.toString()
//                         ? "opacity-100"
//                         : "opacity-0"
//                     )}
//                   />
//                 </CommandItem>
//               ))}
//             {milestones?.filter(
//               (m) =>
//                 !usedMilestones?.includes(m._id) || milestone?._id === m._id
//             ).length === 0 && (
//               <div className="px-2 pb-2">
//                 <div className="w-fit rounded-md bg-red-50 px-2 py-1 text-xs text-red-600">
//                   No milestones available to assign
//                 </div>
//               </div>
//             )}
//           </CommandGroup>
//           <CommandSeparator />
//           <CommandGroup heading="Workspace milestones in use">
//             {milestones
//               ?.filter(
//                 (m) =>
//                   usedMilestones?.includes(m._id) && milestone?._id !== m._id
//               )
//               .map((m) => (
//                 <CommandItem disabled={true} key={m._id} value={m.name}>
//                   <span className="truncate">{m.name}</span>
//                 </CommandItem>
//               ))}
//           </CommandGroup>
//         </Command>
//       </PopoverContent>
//     </Popover>
//   );
// }

// function MilestoneCombobox({
//   taskTemplate,
// }: {
//   taskTemplate: TaskTemplateData;
// }) {
//   const { data: milestones } = useGetMilestones();
//   const { journeyTemplateId } = useParams();
//   const { data: taskTemplates } = useGetTaskTemplates(
//     new ObjectID(journeyTemplateId)
//   );
//   const usedMilestones = useMemo(() => {
//     return (
//       taskTemplates
//         ?.filter((t) => t.milestone?._id)
//         .map((t) => t.milestone?._id.toString()) ?? []
//     );
//   }, [taskTemplates]);
//   const [open, setOpen] = useState(false);
//   const updateTaskTemplateMutation = useUpdateTaskTemplate();
//   const [selectedMilestone, setSelectedMilestone] =
//     useState<MilestoneData | null>(null);
//   return (
//     <Popover open={open} onOpenChange={setOpen}>
//       <PopoverTrigger asChild>
//         <Button
//           variant="secondary"
//           role="combobox"
//           aria-expanded={open}
//           className="h-8 w-40 justify-between px-2 pl-2.5"
//           // disabled={disabled}
//         >
//           <div className="flex items-center space-x-2">
//             <MilestoneIcon />
//             {taskTemplate.milestone ? taskTemplate.milestone.name : ""}
//           </div>
//           <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
//         </Button>
//       </PopoverTrigger>
//       <PopoverContent
//         className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
//         align="start"
//       >
//         <Command>
//           <CommandInput placeholder="Search.." className="h-9" />
//           <CommandEmpty>No member found.</CommandEmpty>
//           <CommandGroup heading="Available Milestones">
//             {milestones
//               ?.filter(
//                 (milestone) =>
//                   !usedMilestones.includes(milestone._id.toString()) ||
//                   milestone._id === taskTemplate.milestone?._id.toString()
//               )
//               .map((milestone) => (
//                 <CommandItem
//                   key={milestone.name}
//                   value={milestone.name}
//                   onSelect={() => {
//                     if (!milestone._id) return;
//                     setSelectedMilestone(milestone);
//                   }}
//                   className="gap-2"
//                 >
//                   {milestone.name}
//                 </CommandItem>
//               ))}
//           </CommandGroup>
//           <CommandGroup heading="Milestones in use">
//             {milestones
//               ?.filter(
//                 (milestone) =>
//                   usedMilestones.includes(milestone._id.toString()) &&
//                   milestone._id !== taskTemplate.milestone?._id.toString()
//               )
//               .map((milestone) => (
//                 <CommandItem
//                   key={milestone.name}
//                   value={milestone.name}
//                   disabled={true}
//                 >
//                   <span className="truncate text-sm">{milestone.name}</span>
//                 </CommandItem>
//               ))}
//           </CommandGroup>
//         </Command>
//       </PopoverContent>
//     </Popover>
//   );
// }

function SubscribersCombobox({
  taskTemplate,
}: {
  taskTemplate: TaskTemplateData;
}) {
  const { journeyTemplateId } = useParams();
  const [open, setOpen] = useState(false);
  const { data: roles } = useGetRoles();
  const updateTaskTemplateMutation = useUpdateTaskTemplate();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          role="combobox"
          aria-expanded={open}
          className="h-8 w-40 justify-between px-2 pl-2.5"
          // disabled={disabled}
        >
          <div className="flex items-center space-x-2">
            <UsersIcon className="h-4 w-4 shrink-0" />
            <div className="flex -space-x-1.5">
              {taskTemplate?.subscribers?.slice(0, 3).map((member) => {
                const role = roles?.find(
                  (r) => r._id.toString() === member._id.toString()
                );
                return role ? getSubscriberAvatar(role.name, role.type) : null;
              })}
              {!!taskTemplate?.subscribers?.length &&
                taskTemplate?.subscribers?.length > 3 && (
                  <div className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-white">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 bg-gray-50 text-xs font-light text-gray-500">
                      <span className="text-[10px]">+</span>
                      {taskTemplate?.subscribers?.length - 3}
                    </div>
                  </div>
                )}
              {!taskTemplate?.subscribers?.length && (
                <div className="font-normal text-gray-400">Subscribers</div>
              )}
            </div>
          </div>
          <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
        {/* <Button
            variant={"secondary"}
            className="h-fit min-h-[36px] w-72 justify-between gap-1 pl-1.5"
          >
            {!!customFieldRecord?.value?.length ? (
              <div className="flex flex-wrap gap-1">
                {customFieldRecord.value.map((value) => (
                  <Badge key={value}>{value}</Badge>
                ))}
              </div>
            ) : (
              <div className="px-1.5 font-normal text-gray-300">
                Select values..
              </div>
            )}
            
          </Button> */}
      </PopoverTrigger>
      <PopoverContent
        className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search.." className="h-9" />
          <CommandList>
            <CommandEmpty>No member found.</CommandEmpty>
            <CommandGroup heading="Internal Roles">
              {roles
                ?.filter((role) => role.type === "internal")
                .map((role) => (
                  <CommandItem
                    key={role.name}
                    value={role.name}
                    onSelect={() => {
                      if (
                        taskTemplate?.subscribers?.find(
                          (m) => m._id.toString() === role._id.toString()
                        )
                      ) {
                        updateTaskTemplateMutation.mutate({
                          journeyTemplateId: taskTemplate.journeyTemplate._id,
                          journeyStageTemplateId:
                            taskTemplate.journeyStageTemplate._id,
                          params: {
                            _id: taskTemplate._id,
                            subscribers: taskTemplate.subscribers.filter(
                              (m) => m._id.toString() !== role._id.toString()
                            ),
                          },
                        });
                      } else {
                        updateTaskTemplateMutation.mutate({
                          journeyTemplateId: taskTemplate.journeyTemplate._id,
                          journeyStageTemplateId:
                            taskTemplate.journeyStageTemplate._id,
                          params: {
                            _id: taskTemplate._id,
                            subscribers: [
                              ...(taskTemplate.subscribers ?? []).map((s) => {
                                return {
                                  _id: s._id,
                                };
                              }),
                              { _id: new ObjectID(role._id) },
                            ],
                          },
                        });
                      }
                    }}
                    className="gap-2"
                  >
                    <Checkbox
                      checked={
                        !!taskTemplate?.subscribers?.find(
                          (m) => m._id.toString() === role._id.toString()
                        )
                      }
                    />
                    <div className="flex items-center space-x-2">
                      <div
                        className={"h-2 w-2 shrink-0 rounded-full bg-gray-300"}
                      />
                      <span className="truncate text-sm">{role.name}</span>
                    </div>
                  </CommandItem>
                ))}
            </CommandGroup>
            <CommandGroup heading="External Roles">
              {roles
                ?.filter((role) => role.type === "external")
                .map((role) => (
                  <CommandItem
                    key={role.name}
                    value={role.name}
                    onSelect={() => {
                      if (
                        taskTemplate?.subscribers?.find(
                          (m) => m._id.toString() === role._id.toString()
                        )
                      ) {
                        updateTaskTemplateMutation.mutate({
                          journeyTemplateId: taskTemplate.journeyTemplate._id,
                          journeyStageTemplateId:
                            taskTemplate.journeyStageTemplate._id,
                          params: {
                            _id: taskTemplate._id,
                            subscribers: taskTemplate.subscribers.filter(
                              (m) => m._id.toString() !== role._id.toString()
                            ),
                          },
                        });
                      } else {
                        updateTaskTemplateMutation.mutate({
                          journeyTemplateId: taskTemplate.journeyTemplate._id,
                          journeyStageTemplateId:
                            taskTemplate.journeyStageTemplate._id,
                          params: {
                            _id: taskTemplate._id,
                            subscribers: [
                              ...(taskTemplate.subscribers ?? []).map((s) => {
                                return {
                                  _id: s._id,
                                };
                              }),
                              { _id: new ObjectID(role._id) },
                            ],
                          },
                        });
                      }
                    }}
                    className="gap-2"
                  >
                    <Checkbox
                      checked={
                        !!taskTemplate?.subscribers?.find(
                          (m) => m._id.toString() === role._id.toString()
                        )
                      }
                    />
                    <div className="flex items-center space-x-2">
                      <div
                        className={"h-2 w-2 shrink-0 rounded-full bg-slate-700"}
                      />
                      <span className="truncate text-sm">{role.name}</span>
                    </div>
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function Description({ taskTemplate }: { taskTemplate: TaskTemplateData }) {
  const updateTaskTemplateMutation = useUpdateTaskTemplate();
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
      Typography,
      Placeholder.configure({
        placeholder: "Add description...",
        showOnlyWhenEditable: false,
      }),
    ],
    content: taskTemplate.description,
    onBlur: ({ editor }) => {
      if (taskTemplate.description.trim() !== editor?.getHTML().trim()) {
        updateTaskTemplateMutation.mutate({
          journeyTemplateId: taskTemplate.journeyTemplate._id,
          journeyStageTemplateId: taskTemplate.journeyStageTemplate._id,
          params: {
            _id: taskTemplate._id,
            description: editor?.getHTML().trim(),
          },
          parentTaskTemplateId: taskTemplate.parentTaskTemplate?._id,
        });
      }
    },
  });

  return <TiptapEditor editor={editor} />;
}

function AddMeetingDialog({
  taskTemplate,
  showMeeting,
  setShowMeeting,
}: {
  taskTemplate: TaskTemplateData;
  showMeeting: boolean;
  setShowMeeting: (value: boolean) => void;
}) {
  const [vendor, setVendor] = useState<MeetingVendor>("calendly");
  const [link, setLink] = useState("");
  const updateTaskTemplateMutation = useUpdateTaskTemplate();
  return (
    <Dialog
      open={showMeeting}
      onOpenChange={(open) => {
        setShowMeeting(open);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Meeting to Task</DialogTitle>
          <DialogDescription>
            Taskers will be able to book a meeting via the link you provide
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-6">
          <div className="space-y-1">
            <InputHeading heading="Meeting Booking Service" />
            <NewSelect
              value={vendor}
              onValueChange={(value) => {
                if (value) {
                  setVendor(value as MeetingVendor);
                }
              }}
            >
              <SelectTrigger className="w-fit gap-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={"calendly"}>{"Calendly"}</SelectItem>
                  <SelectItem value={"hubspot"}>{"HubSpot"}</SelectItem>
                  <SelectItem value={"other"}>{"Other"}</SelectItem>
                </SelectGroup>
              </SelectContent>
            </NewSelect>
          </div>
          <div className="space-y-1">
            <InputHeading heading="Link" />
            <TextInput
              className="w-full max-w-[400px]"
              value={link}
              onChange={(e) => {
                setLink(e.target.value);
              }}
              placeholder={
                vendor === "hubspot"
                  ? "https://meetings.hubspot.com/meeting"
                  : "https://calendly.com/meeting"
              }
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild className="">
            <div className="flex justify-end space-x-3">
              <Button
                variant={"secondary"}
                onClick={() => {
                  setShowMeeting(false);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  updateTaskTemplateMutation.mutate({
                    params: {
                      _id: taskTemplate._id,
                      action: "meeting",
                      meeting: {
                        vendor,
                        link,
                      },
                    },
                    journeyStageTemplateId:
                      taskTemplate.journeyStageTemplate._id,
                    journeyTemplateId: taskTemplate.journeyTemplate._id,
                  });
                  setShowMeeting(false);
                }}
                disabled={link.trim() === ""}
              >
                Add
              </Button>
            </div>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Meeting({ taskTemplate }: { taskTemplate: TaskTemplateData }) {
  const updateTaskTemplateMutation = useUpdateTaskTemplate();
  const [openRemoveMeetingConfirmation, setOpenRemoveMeetingConfirmation] =
    useState(false);
  const [link, setLink] = useState(taskTemplate.meeting?.link ?? "");
  const [error, setError] = useState("");
  return (
    <div className="relative rounded-md border bg-white p-4">
      <Button
        variant={"ghost"}
        className="absolute top-2 right-2 p-1.5 px-2"
        onClick={() => {
          setOpenRemoveMeetingConfirmation(true);
        }}
      >
        <TrashIcon className="h-4 w-4 text-gray-400" />{" "}
      </Button>

      <div className="space-y-6">
        <div className="space-y-1">
          <InputHeading heading="Meeting Booking Service" />
          <NewSelect
            value={taskTemplate.meeting?.vendor}
            onValueChange={(value) => {
              if (value) {
                updateTaskTemplateMutation.mutate({
                  journeyTemplateId: taskTemplate.journeyTemplate._id,
                  journeyStageTemplateId: taskTemplate.journeyStageTemplate._id,
                  params: {
                    _id: taskTemplate._id,
                    action: "meeting",
                    meeting: {
                      vendor: value as MeetingVendor,
                      link: taskTemplate.meeting?.link ?? "",
                    },
                  },
                });
              }
            }}
          >
            <SelectTrigger className="w-fit gap-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value={"calendly"}>{"Calendly"}</SelectItem>
                <SelectItem value={"hubspot"}>{"HubSpot"}</SelectItem>
                <SelectItem value={"other"}>{"Other"}</SelectItem>
              </SelectGroup>
            </SelectContent>
          </NewSelect>
        </div>
        <div className="space-y-1">
          <InputHeading heading="Link" />
          <div className="space-y-1">
            <TextInput
              className="w-full max-w-[300px]"
              value={link}
              onChange={(e) => {
                if (e.target.value.trim() !== "" && error.trim() !== "") {
                  setError("");
                }
                setLink(e.target.value);
              }}
              onBlur={() => {
                if (link.trim() === "") {
                  setLink(taskTemplate.meeting?.link ?? "");
                  setError("Meeting link cannot be empty");
                }
                if (link !== taskTemplate.meeting?.link && link.trim() !== "") {
                  updateTaskTemplateMutation.mutate({
                    journeyTemplateId: taskTemplate.journeyTemplate._id,
                    journeyStageTemplateId:
                      taskTemplate.journeyStageTemplate._id,
                    params: {
                      _id: taskTemplate._id,
                      action: "meeting",
                      meeting: {
                        vendor: taskTemplate.meeting?.vendor as MeetingVendor,
                        link,
                      },
                    },
                  });
                }
              }}
            />
            {error.trim() !== "" && (
              <div className="text-xs text-red-600">{error}</div>
            )}
          </div>
        </div>
      </div>

      <Dialog
        open={openRemoveMeetingConfirmation}
        onOpenChange={(open) => {
          setOpenRemoveMeetingConfirmation(open);
        }}
      >
        <DialogContent
        // onOpenAutoFocus={(e) => {
        //   e.preventDefault();
        // }}
        >
          <DialogHeader>
            <DialogTitle>Remove Meeting</DialogTitle>
            {/* <DialogDescription>Delete all subtasks</DialogDescription> */}
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <p>Are you sure you want to remove the meeting?</p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setOpenRemoveMeetingConfirmation(false);
              }}
              variant={"secondary"}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // remove form template
                updateTaskTemplateMutation.mutate({
                  journeyTemplateId: taskTemplate.journeyTemplate._id,
                  journeyStageTemplateId: taskTemplate.journeyStageTemplate._id,
                  params: {
                    _id: taskTemplate._id,
                    action: "default",
                  },
                });
                setOpenRemoveMeetingConfirmation(false);
              }}
              variant={"destructive"}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FileUpload({ taskTemplate }: { taskTemplate: TaskTemplateData }) {
  const updateTaskTemplateMutation = useUpdateTaskTemplate();
  const [openRemoveFileUploadConfirmation, setOpenRemoveMeetingConfirmation] =
    useState(false);
  return (
    <div className="relative rounded-md border bg-white p-4">
      <Button
        variant={"ghost"}
        className="absolute top-2 right-2 p-1.5 px-2"
        onClick={() => {
          setOpenRemoveMeetingConfirmation(true);
        }}
      >
        <TrashIcon className="h-4 w-4 text-gray-400" />{" "}
      </Button>

      <div className="space-y-6">
        <div>
          <InputHeading heading="File Upload" />
          <div className="text-xs text-gray-500">
            This task will require the user to upload a file
          </div>
        </div>
      </div>

      <Dialog
        open={openRemoveFileUploadConfirmation}
        onOpenChange={(open) => {
          setOpenRemoveMeetingConfirmation(open);
        }}
      >
        <DialogContent
        // onOpenAutoFocus={(e) => {
        //   e.preventDefault();
        // }}
        >
          <DialogHeader>
            <DialogTitle>Remove File Upload</DialogTitle>
            {/* <DialogDescription>Delete all subtasks</DialogDescription> */}
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <p>Are you sure you want to remove the file upload requirement?</p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setOpenRemoveMeetingConfirmation(false);
              }}
              variant={"secondary"}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // remove form template
                updateTaskTemplateMutation.mutate({
                  journeyTemplateId: taskTemplate.journeyTemplate._id,
                  journeyStageTemplateId: taskTemplate.journeyStageTemplate._id,
                  params: {
                    _id: taskTemplate._id,
                    action: "default",
                  },
                });
                setOpenRemoveMeetingConfirmation(false);
              }}
              variant={"destructive"}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Attachments({ taskTemplate }: { taskTemplate: TaskTemplateData }) {
  const [selectedFile, setSelectedFile] = useState<
    TaskTemplateFileData | undefined
  >(undefined);
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteTaskTemplateFileMutation = useDeleteTaskTemplateFile();
  return (
    <div className="pt-4">
      {/* <div className="flex items-center space-x-2">
        <div className="text-sm font-medium text-gray-600">Attachments</div>
        
      </div> */}
      <div className="flex flex-wrap items-center gap-2 text-gray-400">
        {taskTemplate.files?.map((f) => (
          <div className="flex h-8 cursor-default items-center rounded-md border pl-3 pr-1.5 shadow-sm">
            <PaperClipIcon className="mr-2 h-4 w-4 shrink-0 text-gray-400" />
            <div className="pr-1 text-sm font-medium text-gray-500">
              {f.label}
            </div>
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant={"ghost"}
                    className="h-5 w-5 rounded-full p-0.5"
                    onClick={() => {
                      setSelectedFile(f);
                    }}
                  >
                    <TrashIcon className="h-3 w-3 text-gray-400" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent sideOffset={8}>
                  <p>Delete attachment</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant={"ghost"}
                    className="h-5 w-5 rounded-full p-0.5"
                    onClick={() => {
                      window.open(f.url, "_blank");
                    }}
                  >
                    <OpenInNewWindowIcon className="h-3 w-3 text-gray-400" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent sideOffset={8}>
                  <p>Open attachment</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ))}
      </div>
      <Dialog
        open={!!selectedFile}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedFile(undefined);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Confirmation</DialogTitle>
            {/* <DialogDescription>
              Confirm deletion of stage template
            </DialogDescription> */}
          </DialogHeader>
          <div className="space-y-5 pt-3 text-sm text-gray-600">
            <div>
              Are you sure you want to delete the file{" "}
              <span className="font-medium text-gray-900">
                {selectedFile?.name}
              </span>
              ?
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild className="">
              <div className="flex justify-end space-x-3">
                <Button
                  variant={"secondary"}
                  onClick={() => {
                    setSelectedFile(undefined);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant={"destructive"}
                  onClick={() => {
                    if (taskTemplate._id && selectedFile?.name)
                      deleteTaskTemplateFileMutation.mutate({
                        taskTemplateId: taskTemplate._id.toString(),
                        name: selectedFile?.name,
                      });
                  }}
                >
                  {deleteTaskTemplateFileMutation.isLoading
                    ? "Deleting..."
                    : "Delete"}
                </Button>
              </div>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Subtasks({
  taskTemplate,
  addSubtasks,
  setAddSubtasks,
}: {
  taskTemplate: TaskTemplateData;
  addSubtasks: boolean;
  setAddSubtasks: (value: boolean) => void;
}) {
  const [isAddingSubtask, setIsAddingSubtask] = useState(addSubtasks);

  function handleAddSubtask(value: boolean) {
    setIsAddingSubtask(value);
    setAddSubtasks(value);
  }

  return (
    <div className="rounded-md border bg-white">
      <SubtasksView taskTemplate={taskTemplate} />
      {isAddingSubtask && (
        <AddSubtask
          taskTemplate={taskTemplate}
          handleAddSubtask={handleAddSubtask}
        />
      )}
      {!isAddingSubtask && (
        <div className="pl-3 pb-2">
          <button
            className="text-xs text-gray-400 hover:text-gray-600 focus:outline-primary"
            onClick={() => {
              setIsAddingSubtask(true);
            }}
          >
            Add Subtask
          </button>
        </div>
      )}
    </div>
  );
}

function AddSubtask({
  taskTemplate,
  handleAddSubtask,
}: {
  taskTemplate: TaskTemplateData;
  handleAddSubtask: (value: boolean) => void;
}) {
  const [isVisible, setIsVisible] = useState(true);
  const [tasker, setTasker] = useState<TaskTemplateData["tasker"]>(
    taskTemplate.tasker
  );
  // const [taskerRole, setTaskerRole] = useState<TaskTemplateData["taskerRole"]>(
  //   taskTemplate.taskerRole
  // );
  const [title, setTitle] = useState("");
  const [estimate, setEstimate] = useState<TaskTemplateData["estimate"]>({
    value: 10,
    unit: "minute",
  });

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = "0px"; // Reset height to shrink if needed
      const scrollHeight = textAreaRef.current.scrollHeight;
      textAreaRef.current.style.height = scrollHeight + "px";
    }
  }, [title]);
  const createSubTaskTemplateMutation = useCreateSubTaskTemplate();
  console.log(tasker);
  function addSubtask() {
    if (tasker) {
      if (taskTemplate._id) {
        createSubTaskTemplateMutation.mutate({
          journeyStageTemplateId:
            taskTemplate.journeyStageTemplate._id.toString(),
          journeyTemplateId: taskTemplate.journeyTemplate._id.toString(),
          parentTaskTemplateId: taskTemplate._id?.toString(),
          subTaskTemplates: [
            {
              title,
              estimate,
              taskerRole: {
                _id: tasker.role._id.toString(),
              },
              // taskerPersona: tasker.persona,
              taskerType: tasker.type,
              isVisible,
            },
          ],
        });
      }
      // newTaskTemplateCtx.dispatch({
      //   type: "subtasks_update",
      //   subtasks: [
      //     ...newTaskTemplateCtx.state.subtasks,
      //     {
      //       title,
      //       estimate,
      //       id: Math.random(),
      //       tasker: tasker,
      //       isVisible,
      //     },
      //   ],
      // });
      // if (isVisible) {
      //   newTaskTemplateCtx.dispatch({
      //     type: "is_visible_update",
      //     isVisible: true,
      //   });
      // }
      setTitle("");
    }
  }
  const { data: roles } = useGetRoles();

  return (
    <div
      className={clsx(
        "rounded-b-md border-gray-300/60 bg-white p-3 px-3",
        !taskTemplate.subTaskTemplateCount ? "rounded-md" : "mt-2 border-t"
      )}
    >
      <div className="flex items-center gap-1">
        <div className="text-sm text-gray-400">{`New Subtask`}</div>
        {/* <BoltIcon className="h-4 w-4 text-gray-400" />
        <div className="text-sm text-gray-400">
          {newTaskTemplateCtx.state.subtasks.length + 1}
        </div> */}
      </div>
      <div className="space-y-2 pt-1">
        <textarea
          autoFocus
          ref={textAreaRef}
          className="w-full resize-none border-none bg-transparent p-0 outline-none placeholder:text-gray-300 focus:border-none focus:outline-none focus:ring-0"
          value={title}
          onChange={(event) => {
            if (event.target.value.includes("\n")) {
              if (title.trim() !== "") {
                addSubtask();
              } else {
                return;
              }
            } else {
              setTitle(event.target.value);
            }
          }}
          placeholder={`Subtask title`}
        />
        <div className="flex justify-between gap-2">
          <div className="flex space-x-2">
            {tasker?.role && (
              <SubtaskRoleCombobox
                taskerType={tasker?.type ?? "internal"}
                taskerRole={tasker?.role}
                onExternalTaskerChange={(externalTasker) => {
                  const role = roles?.find(
                    (r) => r._id.toString() === externalTasker
                  );
                  if (role) {
                    setTasker({
                      type: "external",
                      role: {
                        _id: new ObjectID(role._id),
                        name: role.name,
                      },
                    });
                    setIsVisible(true);
                  }
                }}
                onInternalTaskerChange={(internalTasker) => {
                  const role = roles?.find(
                    (r) => r._id.toString() === internalTasker
                  );

                  if (role) {
                    setTasker({
                      type: "internal",
                      role: {
                        _id: new ObjectID(internalTasker),
                        name: role.name,
                      },
                    });
                  }
                }}
              />
            )}
            <SubtaskDurationSelect
              widthFit={true}
              estimate={estimate}
              value={estimate.value + " " + estimate.unit}
              onValueChange={(value: string) => {
                const e = estimateOptions.find(
                  (estimate) => estimate.value + " " + estimate.unit === value
                );
                if (e) {
                  setEstimate(e);
                }
                if (value === "0 hour") {
                  setEstimate({ value: 0, unit: "hour" });
                }
              }}
            />
            <SubtaskVisibilityButtons
              isVisible={isVisible}
              onValueChange={(value) => {
                setIsVisible(value);
              }}
              taskerType={tasker?.type ?? "internal"}
            />
            {/* <NewSelect
                value={estimate.value + " " + estimate.unit}
                onValueChange={(value) => {
                  const e = estimateOptions.find(
                    (estimate) => estimate.value + " " + estimate.unit === value
                  );
                  if (e) {
                    setEstimate(e);
                  }
                  if (value === "0 hour") {
                    setEstimate({ value: 0, unit: "hour" });
                  }
                }}
              >
                <SelectTrigger className="h-7 w-fit gap-2 bg-white px-2">
                  <ClockIcon className="h-4 w-4 text-gray-700" />
                  <SelectValue>
                    {estimate.value === 0
                      ? "Not sure"
                      : estimate.value +
                        " " +
                        (estimate.unit === "minute" ? "min" : "hr")}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value={"0 hour"}>Not sure</SelectItem>
                    {estimateOptions.map((estimate) => (
                      <SelectItem
                        key={estimate.value + estimate.unit}
                        value={estimate.value + " " + estimate.unit}
                      >
                        {estimate.value +
                          " " +
                          (estimate.unit === "minute" ? "min" : "hr")}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </NewSelect> */}
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                handleAddSubtask(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => {
                addSubtask();
              }}
              disabled={title.trim() === "" || !tasker}
            >
              Add
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SubtasksView({ taskTemplate }: { taskTemplate: TaskTemplateData }) {
  const { subTaskTemplates } = useGetSubTaskTemplates(
    new ObjectID(taskTemplate._id)
  );
  const updateTaskTemplateMutation = useUpdateTaskTemplate();
  const handleDragEnd = (event: DragEndEvent) => {
    const { over, active } = event;
    if (over && active) {
      const { id: overId } = over;
      const { id: activeId } = active;
      if (overId !== activeId && taskTemplate.subTaskTemplateOrder) {
        const oldIndex = taskTemplate.subTaskTemplateOrder.findIndex(
          (tid) => tid?.toString() === activeId
        );
        const newIndex = taskTemplate.subTaskTemplateOrder.findIndex(
          (tid) => tid?.toString() === overId
        );
        const newSubTaskTemplateOrder = arrayMove(
          taskTemplate.subTaskTemplateOrder,
          oldIndex,
          newIndex
        );
        updateTaskTemplateMutation.mutate({
          journeyTemplateId: taskTemplate.journeyTemplate._id,
          journeyStageTemplateId: taskTemplate.journeyStageTemplate._id,
          params: {
            _id: taskTemplate._id,
            subTaskTemplateOrder: newSubTaskTemplateOrder,
          },
        });
      }
    }
  };

  return (
    <div className="pt-3 pb-1">
      <div className="px-3 text-sm font-light text-gray-500">{`Subtasks`}</div>
      {/* <BoltIcon className="h-4 w-4 text-gray-400" />
                <div className="text-sm text-gray-400">
                  {newTaskTemplateCtx.state.subtasks.length}
                </div> */}

      {taskTemplate.subTaskTemplateOrder && (
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={taskTemplate.subTaskTemplateOrder?.map((t) => t.toString())}
            strategy={verticalListSortingStrategy}
          >
            {taskTemplate.subTaskTemplateOrder.map((t) => {
              const subtaskTemplate = subTaskTemplates?.find(
                (tt) => tt._id?.toString() === t.toString()
              );
              if (subtaskTemplate) {
                return (
                  <SubtaskTemplate
                    subtaskTemplate={subtaskTemplate}
                    subtaskTemplateId={String(subtaskTemplate._id?.toString())}
                  />
                  // <TaskTemplate
                  //   key={subTaskTemplate._id?.toString() ?? Math.random()}
                  //   taskTemplate={subTaskTemplate}
                  //   isSubTask={true}
                  //   taskTemplateId={
                  //     subTaskTemplate._id?.toString() ??
                  //     Math.random().toString()
                  //   }
                  // />
                );
              }
              return null;
            })}

            {/* {isExpanded && (
          <div className="">
            {subTaskTemplates?.map((st) => (
              <TaskTemplate
                key={st._id?.toString() ?? Math.random()}
                taskTemplate={st}
                isSubTask={true}
                taskTemplateId={st._id?.toString() ?? Math.random().toString()}
              />
            ))}
          </div>
        )} */}
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

function SubtaskTemplate({
  subtaskTemplate,
  subtaskTemplateId,
}: {
  subtaskTemplate: TaskTemplateData;
  subtaskTemplateId: string;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: subtaskTemplateId,
    });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };
  const taskTemplatePanelCtx = useTaskTemplatePanelContext();
  const { data: roles } = useGetRoles();
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="group relative flex cursor-pointer items-center justify-between gap-2 px-3 py-1 pl-3 hover:bg-gray-100"
      onClick={() => {
        // navigate
        taskTemplatePanelCtx.dispatch({
          type: "TASK_TEMPLATE_PANEL_UPDATE",
          taskTemplatePanel: {
            id: subtaskTemplateId,
            isOpen: true,
          },
        });
      }}
      tabIndex={-1}
    >
      <DragHandleDots2Icon
        className="absolute right-full h-5 w-5 cursor-move text-transparent transition-colors group-hover:text-gray-300"
        {...listeners}
      />
      <div className="flex items-center gap-1">
        <div className="truncate">{subtaskTemplate.title}</div>
      </div>
      <div className="flex items-start gap-2">
        <div className="flex gap-1">
          <div className="inline-flex h-7 items-center gap-1 whitespace-nowrap rounded-md border border-gray-300 bg-gray-50 px-1.5 pr-2 text-sm">
            <UserIcon className="h-3.5 w-3.5 text-gray-700" />
            <div
              className={clsx(
                "mx-0.5 h-1.5 w-1.5 rounded-full",
                subtaskTemplate.tasker?.type === "internal"
                  ? "bg-gray-300"
                  : "bg-slate-700"
              )}
            />
            <span className="text-xs text-gray-700">
              {subtaskTemplate.tasker?.role?.name}
            </span>
          </div>
          <div className="inline-flex h-7 items-center gap-1 whitespace-nowrap rounded-md border border-gray-300 bg-gray-50 px-1.5 text-sm">
            <StopwatchIcon className="h-3.5 w-3.5 text-gray-700" />
            <span className="text-xs text-gray-700">
              {subtaskTemplate.estimate.value +
                " " +
                (subtaskTemplate.estimate.unit === "minute" ? "min" : "hr")}
            </span>
          </div>
          <div className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 bg-gray-50">
            {subtaskTemplate.isVisible ? (
              <EyeIcon className="h-4 w-4 text-gray-600" />
            ) : (
              <EyeSlashIcon className="h-4 w-4 text-gray-600" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MeetingAction({
  setShowMeeting,
}: {
  setShowMeeting: (value: boolean) => void;
}) {
  return (
    <div>
      <Action
        icon={<PhoneIcon className="h-5 w-5 text-gray-400" />}
        onClick={() => {
          setShowMeeting(true);
        }}
        text="Meeting"
        // disabled={!!taskTemplate.subTaskTemplateCount || addSubtasks}
        // active={taskTemplate.action === "form" || showFormTemplate}
      />
    </div>
  );
}

function FileUploadAction({
  setShowFileUpload,
  taskTemplate,
}: {
  setShowFileUpload: (value: boolean) => void;
  taskTemplate: TaskTemplateData;
}) {
  const updateTaskTemplateMutation = useUpdateTaskTemplate();
  return (
    <div>
      <Action
        icon={<CloudArrowUpIcon className="h-5 w-5 text-gray-400" />}
        onClick={() => {
          setShowFileUpload(true);
          updateTaskTemplateMutation.mutate({
            journeyTemplateId: taskTemplate.journeyTemplate._id,
            journeyStageTemplateId: taskTemplate.journeyStageTemplate._id,
            params: {
              _id: taskTemplate._id,
              action: "fileUpload",
            },
          });
        }}
        text="File Upload"
        // disabled={!!taskTemplate.subTaskTemplateCount || addSubtasks}
        // active={taskTemplate.action === "form" || showFormTemplate}
      />
    </div>
  );
}

function SubtasksAction({
  setAddSubtasks,
}: {
  setAddSubtasks: (value: boolean) => void;
}) {
  return (
    <div>
      <Action
        icon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            className="text-gray-400"
          >
            <path
              fill="currentColor"
              d="M17 16a2.99 2.99 0 0 0-2.816 2H11c-1.654 0-3-1.346-3-3v-3.025A4.954 4.954 0 0 0 11 13h3.184A2.99 2.99 0 0 0 17 15a3 3 0 1 0 0-6a2.99 2.99 0 0 0-2.816 2H11c-1.654 0-3-1.346-3-3v-.184A2.99 2.99 0 0 0 10 5a3 3 0 1 0-6 0a2.99 2.99 0 0 0 2 2.816V15c0 2.757 2.243 5 5 5h3.184A2.99 2.99 0 0 0 17 22a3 3 0 1 0 0-6zm0-5a1.001 1.001 0 1 1-1 1c0-.551.448-1 1-1zM7 4a1.001 1.001 0 1 1-1 1c0-.551.448-1 1-1zm10 16a1.001 1.001 0 0 1 0-2a1.001 1.001 0 0 1 0 2z"
              // stroke="currentColor"
              strokeWidth={0}
            />
          </svg>
        }
        onClick={() => {
          setAddSubtasks(true);
        }}
        text="Subtasks"
        // disabled={!!taskTemplate.subTaskTemplateCount || addSubtasks}
        // active={taskTemplate.action === "form" || showFormTemplate}
      />
    </div>
  );
}

function FormAction({
  taskTemplate,
  setShowFormTemplate,
  showFormTemplate,
  addSubtasks,
}: {
  taskTemplate: TaskTemplateData;
  setShowFormTemplate: (show: boolean) => void;
  showFormTemplate: boolean;
  addSubtasks: boolean;
}) {
  return (
    <>
      <Action
        icon={<DocumentTextIcon className="h-5 w-5 text-gray-400" />}
        onClick={() => {
          if (taskTemplate.action === "form") {
            // setOpenRemoveFormConfirmation(true);
          } else if (showFormTemplate) {
            setShowFormTemplate(false);
          } else {
            setShowFormTemplate(true);
          }
        }}
        text="Form"
        disabled={!!taskTemplate.subTaskTemplateCount || addSubtasks}
        active={taskTemplate.action === "form" || showFormTemplate}
      />
    </>
  );
}

function FormTemplateCombobox({
  taskTemplate,
}: {
  taskTemplate: TaskTemplateData;
}) {
  const updateTaskTemplateMutation = useUpdateTaskTemplate();
  const { data: formTemplates } = useGetFormTemplates();
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          role="combobox"
          aria-expanded={open}
          className="space-x-2 truncate"
        >
          <DocumentTextIcon className="h-4 w-4 shrink-0" />
          {taskTemplate.formTemplate ? (
            <div className="truncate">
              {
                formTemplates?.find(
                  (ft) => ft._id === taskTemplate.formTemplate?._id.toString()
                )?.title
              }
            </div>
          ) : (
            <div className="flex space-x-1 text-gray-400/80">
              Form Template
              <span className="top-0 ml-0.5 text-primary-main">*</span>
            </div>
          )}
          <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search form template..." className="h-9" />
          <CommandList>
            <CommandEmpty>No form template found.</CommandEmpty>
            <CommandGroup>
              {formTemplates?.map((formTemplate) => (
                <CommandItem
                  key={formTemplate._id}
                  value={formTemplate.title}
                  onSelect={() => {
                    if (
                      taskTemplate.formTemplate?._id.toString() !==
                      formTemplate._id.toString()
                    ) {
                      updateTaskTemplateMutation.mutate({
                        journeyTemplateId: taskTemplate.journeyTemplate._id,
                        journeyStageTemplateId:
                          taskTemplate.journeyStageTemplate._id,
                        params: {
                          _id: taskTemplate._id,
                          action: "form",
                          formTemplateId: new ObjectID(formTemplate._id),
                        },
                      });
                    }
                    setOpen(false);
                  }}
                  className="gap-2"
                >
                  <span className="truncate">{formTemplate.title}</span>
                  <CheckIcon
                    className={cn(
                      "ml-auto h-4 w-4 shrink-0",
                      taskTemplate.formTemplate?._id.toString() ===
                        formTemplate._id.toString()
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function Title({ taskTemplate }: { taskTemplate: TaskTemplateData }) {
  const updateTaskTemplateMutation = useUpdateTaskTemplate();
  const [title, setTitle] = useState(taskTemplate.title);

  useEffect(() => {
    setTitle(taskTemplate.title);
  }, [taskTemplate.title]);

  return (
    <ReusableTitle
      title={title}
      onChange={(e) => {
        setTitle(e.target.value);
      }}
      onBlur={(e) => {
        if (title.trim() !== taskTemplate.title) {
          updateTaskTemplateMutation.mutate({
            journeyTemplateId: taskTemplate.journeyTemplate._id,
            journeyStageTemplateId: taskTemplate.journeyStageTemplate._id,
            params: {
              _id: taskTemplate._id,
              title: title,
            },
            parentTaskTemplateId: taskTemplate.parentTaskTemplate?._id,
          });
        }
      }}
    />
  );
}

function SubtasksButton({
  taskTemplate,
  setAddSubtasks,
  addSubtasks,
}: {
  taskTemplate: TaskTemplateData;
  setAddSubtasks: (show: boolean) => void;
  addSubtasks: boolean;
}) {
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  return (
    <>
      <Button
        onClick={() => {
          setAddSubtasks(true);
        }}
        disabled={taskTemplate.action !== "default"}
        variant={"secondary"}
        className="space-x-2"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
        >
          <path
            fill="currentColor"
            d="M17 16a2.99 2.99 0 0 0-2.816 2H11c-1.654 0-3-1.346-3-3v-3.025A4.954 4.954 0 0 0 11 13h3.184A2.99 2.99 0 0 0 17 15a3 3 0 1 0 0-6a2.99 2.99 0 0 0-2.816 2H11c-1.654 0-3-1.346-3-3v-.184A2.99 2.99 0 0 0 10 5a3 3 0 1 0-6 0a2.99 2.99 0 0 0 2 2.816V15c0 2.757 2.243 5 5 5h3.184A2.99 2.99 0 0 0 17 22a3 3 0 1 0 0-6zm0-5a1.001 1.001 0 1 1-1 1c0-.551.448-1 1-1zM7 4a1.001 1.001 0 1 1-1 1c0-.551.448-1 1-1zm10 16a1.001 1.001 0 0 1 0-2a1.001 1.001 0 0 1 0 2z"
            // stroke="currentColor"
            strokeWidth={0}
          />
        </svg>
        <span>{"Add Subtasks"}</span>
      </Button>
      {/* <Button
        onClick={() => {
          if (!!taskTemplate.subTaskTemplateCount) {
            // confirm delete of all subtasks
            setOpenDeleteConfirmation(true);
          } else if (addSubtasks) {
            setAddSubtasks(false);
          } else {
            setAddSubtasks(true);
          }
        }}
        disabled={taskTemplate.action !== "default"}
        variant={"secondary"}
      >
        {!!taskTemplate.subTaskTemplateCount || addSubtasks
          ? "Delete Subtasks"
          : "Add Subtasks"}
      </Button>
      <Dialog
        open={openDeleteConfirmation}
        onOpenChange={(open) => {
          setOpenDeleteConfirmation(open);
        }}
      >
        <DialogContent
        // onOpenAutoFocus={(e) => {
        //   e.preventDefault();
        // }}
        >
          <DialogHeader>
            <DialogTitle>Delete Subtasks</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <p>Are you sure you want to delete all the subtasks?</p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setOpenDeleteConfirmation(false);
              }}
              variant={"secondary"}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // delete all subtasks
              }}
              variant={"destructive"}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog> */}
    </>
  );
}

function Visibility({ taskTemplate }: { taskTemplate: TaskTemplateData }) {
  const updateTaskTemplateMutation = useUpdateTaskTemplate();
  return (
    <Select
      value={taskTemplate.isVisible ? "visible" : "hidden"}
      onValueChange={(value) => {
        updateTaskTemplateMutation.mutate({
          journeyTemplateId: taskTemplate.journeyTemplate._id,
          params: {
            _id: taskTemplate._id,
            isVisible: value === "visible",
          },
          journeyStageTemplateId: taskTemplate.journeyStageTemplate?._id,
          parentTaskTemplateId: taskTemplate.parentTaskTemplate?._id,
        });
      }}
    >
      <SelectTrigger className="h-8 w-44">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="visible">
          <div className="flex items-center space-x-2">
            <EyeIcon className="h-4 w-4" />
            <span>Externally visible</span>
          </div>
        </SelectItem>
        <SelectItem value="hidden">
          <div className="flex items-center space-x-2">
            <EyeSlashIcon className="h-4 w-4" />
            <span>Externally hidden</span>
          </div>
        </SelectItem>
      </SelectContent>
    </Select>
    // <div className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 px-1 shadow-sm">
    //   <TooltipProvider>
    //     <Tooltip delayDuration={0}>
    //       <TooltipTrigger asChild>
    //         <button
    //           className={clsx(
    //             "rounded p-1 outline-none focus-visible:ring-[1.25px] focus-visible:ring-primary",
    //             taskTemplate.isVisible
    //               ? "bg-primary-100 text-primary-600"
    //               : "text-gray-500 hover:bg-gray-100"
    //           )}
    //           onClick={() => {
    //             updateTaskTemplateMutation.mutate({
    //               journeyTemplateId: taskTemplate.journeyTemplate._id,
    //               params: {
    //                 _id: taskTemplate._id,
    //                 isVisible: true,
    //               },
    //               journeyStageTemplateId:
    //                 taskTemplate.journeyStageTemplate?._id,
    //               parentTaskTemplateId: taskTemplate.parentTaskTemplate?._id,
    //             });
    //           }}
    //         >
    //           <EyeIcon className="h-4 w-4" />
    //         </button>
    //       </TooltipTrigger>
    //       <TooltipContent sideOffset={8}>
    //         <p>Externally visible</p>
    //       </TooltipContent>
    //     </Tooltip>
    //   </TooltipProvider>
    //   <TooltipProvider>
    //     <Tooltip delayDuration={0}>
    //       <TooltipTrigger asChild>
    //         <button
    //           className={clsx(
    //             "rounded p-1 outline-none focus-visible:ring-[1.25px] focus-visible:ring-primary",
    //             !taskTemplate.isVisible
    //               ? "bg-primary-100 text-primary-600"
    //               : "text-gray-500 hover:bg-gray-100",
    //             taskTemplate.tasker?.type == "external" &&
    //               "bg-gray-100 text-gray-300 hover:bg-gray-100"
    //           )}
    //           onClick={() => {
    //             updateTaskTemplateMutation.mutate({
    //               journeyTemplateId: taskTemplate.journeyTemplate._id,
    //               params: {
    //                 _id: taskTemplate._id,
    //                 isVisible: false,
    //               },
    //               journeyStageTemplateId:
    //                 taskTemplate.journeyStageTemplate?._id,
    //               parentTaskTemplateId: taskTemplate.parentTaskTemplate?._id,
    //             });
    //           }}
    //           disabled={taskTemplate.tasker?.type == "external"}
    //         >
    //           <EyeSlashIcon
    //             className={`h-4 w-4 ${
    //               taskTemplate.tasker?.type === "external"
    //                 ? "text-gray-300"
    //                 : ""
    //             }`}
    //           />
    //         </button>
    //       </TooltipTrigger>
    //       <TooltipContent sideOffset={8}>
    //         <p>Externally hidden</p>
    //       </TooltipContent>
    //     </Tooltip>
    //   </TooltipProvider>
    // </div>
  );
}
function DueDateDependency({
  taskTemplate,
}: {
  taskTemplate: TaskTemplateData;
}) {
  const updateTaskTemplateMutation = useUpdateTaskTemplate();
  const [launchCondition, setLaunchCondition] =
    useState<TaskTemplateLaunchCondition>(
      taskTemplate.dependentOn.type === "default"
        ? "project start"
        : taskTemplate.dependentOn.type === "task"
        ? "completion of task"
        : "completion of stage"
    );
  const [value, setValue] = useState(
    String(taskTemplate.relativeDueDate.value)
  );
  const [taskTemplatesComboboxOpen, setTaskTemplatesComboboxOpen] =
    useState(false);
  const [stageTemplatesComboboxOpen, setStageTemplatesComboboxOpen] =
    useState(false);
  const { stageAndTaskTemplateList } = useGetStageAndTaskTemplatesList(
    taskTemplate.journeyTemplate._id
  );

  useEffect(() => {
    setValue(String(taskTemplate.relativeDueDate.value));
  }, [taskTemplate.relativeDueDate.value]);

  return (
    <div className="flex items-center gap-2 text-sm text-gray-600">
      <span className="-mr-0.5 text-gray-500">Due</span>
      <div>
        <TextInput
          value={value}
          className="h-8 w-[70px]"
          onChange={(e) => {
            const rx = new RegExp(/^\d+$/);
            if (rx.test(e.target.value) || e.target.value === "") {
              setValue(e.target.value);
            }
          }}
          onBlur={(e) => {
            if (value === "") {
              updateTaskTemplateMutation.mutate({
                params: {
                  _id: taskTemplate._id,
                  relativeDueDate: {
                    ...taskTemplate.relativeDueDate,
                    value: 1,
                  },
                },
                journeyStageTemplateId: taskTemplate.journeyStageTemplate._id,
                journeyTemplateId: taskTemplate.journeyTemplate._id,
              });
            } else {
              updateTaskTemplateMutation.mutate({
                params: {
                  _id: taskTemplate._id,
                  relativeDueDate: {
                    ...taskTemplate.relativeDueDate,
                    value: parseInt(e.target.value),
                  },
                },
                journeyStageTemplateId: taskTemplate.journeyStageTemplate._id,
                journeyTemplateId: taskTemplate.journeyTemplate._id,
              });
            }
          }}
        />
      </div>
      <div>
        <NewSelect
          value={taskTemplate.relativeDueDate.unit}
          onValueChange={(value) => {
            updateTaskTemplateMutation.mutate({
              params: {
                _id: taskTemplate._id,
                relativeDueDate: {
                  ...taskTemplate.relativeDueDate,
                  unit: value as (typeof relativeDueDateUnits)[number],
                },
              },
              journeyStageTemplateId: taskTemplate.journeyStageTemplate._id,
              journeyTemplateId: taskTemplate.journeyTemplate._id,
            });
          }}
        >
          <SelectTrigger className="w-fit gap-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {relativeDueDateUnits
                .filter(
                  (unit) =>
                    unit !== "minute" && unit !== "month" && unit !== "quarter"
                )
                .map((unit) => (
                  <SelectItem value={unit}>{unit + "s"}</SelectItem>
                ))}
            </SelectGroup>
          </SelectContent>
        </NewSelect>
      </div>
      <span className="-mx-0.5 text-gray-500">after</span>
      <NewSelect
        value={launchCondition}
        onValueChange={(value) => {
          if (value === "project start") {
            console.log(taskTemplate.dependentOn);
            setLaunchCondition("project start");
            updateTaskTemplateMutation.mutate({
              journeyTemplateId: taskTemplate.journeyTemplate._id,
              params: {
                _id: taskTemplate._id,
                dependentOn: {
                  type: "default",
                  ...(!!taskTemplate.dependentOn.dependencyTemplateId
                    ? {
                        dependencyTemplateId:
                          taskTemplate.dependentOn.dependencyTemplateId,
                      }
                    : {}),
                },
              },
              journeyStageTemplateId: taskTemplate.journeyStageTemplate._id,
            });
          } else {
            setLaunchCondition(value as TaskTemplateLaunchCondition);
          }
        }}
      >
        <SelectTrigger className="w-fit gap-2">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {taskTemplateLaunchConditions.map((condition) => (
              <SelectItem value={condition}>{condition}</SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </NewSelect>
      {launchCondition === "completion of task" && (
        <Popover
          open={taskTemplatesComboboxOpen}
          onOpenChange={setTaskTemplatesComboboxOpen}
        >
          <PopoverTrigger asChild>
            <Button
              variant="secondary"
              role="combobox"
              aria-expanded={taskTemplatesComboboxOpen}
              className="max-w-[200px] space-x-2 truncate"
            >
              <BoltIcon className="h-4 w-4 shrink-0" />
              {taskTemplate.dependentOn.type === "task" &&
              taskTemplate.dependentOn.name ? (
                <div className="truncate">{taskTemplate.dependentOn.name}</div>
              ) : (
                <div className="flex space-x-1 text-gray-400/80">
                  Task
                  <span className="top-0 ml-0.5 text-primary-main">*</span>
                </div>
              )}
              <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
            align="end"
          >
            <Command
              filter={(value, search) => {
                return stageAndTaskTemplateList.taskTemplates
                  .find((t) => t._id.toString() === value)
                  ?.name.toLowerCase()
                  .includes(search.toLowerCase())
                  ? 1
                  : 0;
              }}
            >
              <CommandInput placeholder="Search taskk..." className="h-9" />
              <CommandList>
                <CommandEmpty>No task found.</CommandEmpty>
                {stageAndTaskTemplateList.stageTemplates.map((s) => (
                  <CommandGroup heading={s.name}>
                    {stageAndTaskTemplateList.taskTemplates
                      .filter(
                        (t) =>
                          t.journeyStageTemplate._id.toString() ===
                          s._id.toString()
                      )
                      .map((tt) => (
                        <TooltipProvider delayDuration={1200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <CommandItem
                                key={tt._id.toString()}
                                value={tt._id.toString()}
                                onSelect={() => {
                                  updateTaskTemplateMutation.mutate({
                                    params: {
                                      _id: taskTemplate._id,
                                      dependentOn: {
                                        type: "task",
                                        _id: tt._id,
                                        ...(!!taskTemplate.dependentOn
                                          .dependencyTemplateId
                                          ? {
                                              dependencyTemplateId:
                                                taskTemplate.dependentOn
                                                  .dependencyTemplateId,
                                            }
                                          : {}),
                                      },
                                    },
                                    journeyStageTemplateId:
                                      taskTemplate.journeyStageTemplate._id,
                                    journeyTemplateId:
                                      taskTemplate.journeyTemplate._id,
                                  });
                                  setTaskTemplatesComboboxOpen(false);
                                }}
                                className="gap-2"
                              >
                                <span className="truncate">{tt.name}</span>
                                <CheckIcon
                                  className={cn(
                                    "ml-auto h-4 w-4 shrink-0",
                                    taskTemplate.dependentOn?._id?.toString() ===
                                      tt._id.toString()
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                              </CommandItem>
                            </TooltipTrigger>
                            <TooltipContent
                              align="start"
                              side="left"
                              sideOffset={12}
                            >
                              <p>{tt.journeyStageTemplate.name}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ))}
                  </CommandGroup>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
      {launchCondition === "completion of stage" && (
        <Popover
          open={stageTemplatesComboboxOpen}
          onOpenChange={setStageTemplatesComboboxOpen}
        >
          <PopoverTrigger asChild>
            <Button
              variant="secondary"
              role="combobox"
              aria-expanded={stageTemplatesComboboxOpen}
              className="max-w-[200px] space-x-2 truncate"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                className="h-4 w-4 shrink-0"
              >
                <path d="M7 22V2l10 5-10 5" />
              </svg>
              {taskTemplate.dependentOn.type === "stage" &&
              taskTemplate.dependentOn.name ? (
                <div className="truncate">{taskTemplate.dependentOn.name}</div>
              ) : (
                <div className="flex space-x-1 text-gray-400/80">
                  Stage
                  <span className="top-0 ml-0.5 text-primary-main">*</span>
                </div>
              )}
              <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
            align="end"
          >
            <Command>
              <CommandInput placeholder="Search stage..." className="h-9" />
              <CommandList>
                <CommandEmpty>No stage found.</CommandEmpty>
                <CommandGroup>
                  {stageAndTaskTemplateList.stageTemplates
                    .filter(
                      (s) =>
                        s._id.toString() !==
                        taskTemplate.journeyStageTemplate?._id?.toString()
                    )
                    .map((st) => (
                      <CommandItem
                        key={st._id.toString()}
                        value={st.name}
                        onSelect={() => {
                          updateTaskTemplateMutation.mutate({
                            params: {
                              _id: taskTemplate._id,
                              dependentOn: {
                                type: "stage",
                                _id: st._id,
                                ...(!!taskTemplate.dependentOn
                                  .dependencyTemplateId
                                  ? {
                                      dependencyTemplateId:
                                        taskTemplate.dependentOn
                                          .dependencyTemplateId,
                                    }
                                  : {}),
                              },
                            },
                            journeyStageTemplateId:
                              taskTemplate.journeyStageTemplate._id,
                            journeyTemplateId: taskTemplate.journeyTemplate._id,
                          });
                          setStageTemplatesComboboxOpen(false);
                        }}
                        className="gap-2"
                      >
                        <span className="truncate">{st.name}</span>
                        <CheckIcon
                          className={cn(
                            "ml-auto h-4 w-4 shrink-0",
                            taskTemplate.dependentOn?._id?.toString() ===
                              st._id.toString()
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />
                      </CommandItem>
                    ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

function DurationSelect({ taskTemplate }: { taskTemplate: TaskTemplateData }) {
  const updateTaskTemplateMutation = useUpdateTaskTemplate();
  return (
    <NewSelect
      value={taskTemplate.estimate.value + " " + taskTemplate.estimate.unit}
      onValueChange={(value) => {
        const e = estimateOptions.find(
          (estimate) => estimate.value + " " + estimate.unit === value
        );
        if (e) {
          updateTaskTemplateMutation.mutate({
            params: {
              _id: taskTemplate._id,
              estimate: e,
            },
            journeyStageTemplateId: taskTemplate.journeyStageTemplate._id,
            journeyTemplateId: taskTemplate.journeyTemplate._id,
            parentTaskTemplateId: taskTemplate.parentTaskTemplate?._id,
          });
        }
      }}
    >
      <SelectTrigger className="w-fit gap-2">
        <StopwatchIcon className="h-4 w-4 text-gray-700" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {/* <SelectLabel>Fruits</SelectLabel> */}
          {estimateOptions.map((estimate) => (
            <SelectItem value={estimate.value + " " + estimate.unit}>
              {estimate.value +
                " " +
                (estimate.unit === "minute" ? "min" : "hr")}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </NewSelect>
  );
}

function SubtasksDurationSum({
  taskTemplate,
}: {
  taskTemplate: TaskTemplateData;
}) {
  const { subTaskTemplates } = useGetSubTaskTemplates(
    new ObjectID(taskTemplate._id)
  );
  const totalDuration = (subTaskTemplates ?? []).reduce((acc, task) => {
    if (task.estimate) {
      return acc + estimateToMinutes(task.estimate);
    }
    return acc;
  }, 0);
  // console.log(totalDuration, tasks[0].status);
  const hours = Math.floor(totalDuration / 60);
  const minutes = totalDuration % 60;

  if (totalDuration > 0) {
    if (minutes === 0) {
      return (
        <div className="flex h-8 items-center space-x-2 rounded-md border border-gray-300 bg-gray-100 px-2 text-sm">
          <StopwatchIcon className="h-4 w-4 text-gray-700" />
          <span>{`${hours} hr`}</span>
        </div>
      );
    } else {
      if (hours === 0) {
        return (
          <div className="flex h-8 items-center space-x-2 rounded-md border border-gray-300 bg-gray-100 px-2 text-sm">
            <StopwatchIcon className="h-4 w-4 text-gray-700" />
            <span>{`${minutes} min`}</span>
          </div>
        );
      }
      return (
        <div className="flex h-8 items-center space-x-2 rounded-md border border-gray-300 bg-gray-100 px-2 text-sm">
          <StopwatchIcon className="h-4 w-4 text-gray-700" />
          <span>{`${hours} hr, ${minutes} min`}</span>
        </div>
      );
    }
  } else {
    return (
      <div className="flex h-8 items-center space-x-2 rounded-md border border-gray-300 bg-gray-100 px-2 text-sm">
        <StopwatchIcon className="h-4 w-4 text-gray-700" />
        <span>{`0 hr`}</span>
      </div>
    );
  }
}

function StageCombobox({ taskTemplate }: { taskTemplate: TaskTemplateData }) {
  const { stageAndTaskTemplateList } = useGetStageAndTaskTemplatesList(
    taskTemplate.journeyTemplate._id
  );
  const updateTaskTemplateMutation = useUpdateTaskTemplate();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          role="combobox"
          aria-expanded={open}
          className="max-w-[200px] space-x-2 truncate"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            className="h-4 w-4"
          >
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
            <line x1="4" x2="4" y1="22" y2="15" />
          </svg>
          {taskTemplate.journeyStageTemplate ? (
            <div className="truncate">
              {taskTemplate.journeyStageTemplate.name}
            </div>
          ) : (
            <div className="text-gray-400/80">Stage</div>
          )}
          <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search stage..." className="h-9" />
          <CommandList>
            <CommandEmpty>No stage found.</CommandEmpty>
            <CommandGroup>
              {stageAndTaskTemplateList.stageTemplates.map((stageTemplate) => (
                <CommandItem
                  key={stageTemplate._id.toString()}
                  value={stageTemplate.name}
                  onSelect={() => {
                    let params: Partial<TaskTemplateUpdateRequest> = {
                      _id: taskTemplate._id,
                      journeyStageTemplateId: stageTemplate._id,
                    };
                    if (
                      taskTemplate.journeyStageTemplate?._id?.toString() ===
                      stageTemplate._id?.toString()
                    ) {
                      params["dependentOn"] = {
                        name: "Project Start",
                        type: "default",
                      };
                    }

                    updateTaskTemplateMutation.mutate({
                      journeyTemplateId: taskTemplate.journeyTemplate._id,
                      previousJourneyStageId:
                        taskTemplate.journeyStageTemplate._id,
                      journeyStageTemplateId: stageTemplate._id,
                      params,
                    });
                    setOpen(false);
                  }}
                  className="gap-2"
                >
                  <span className="truncate">{stageTemplate.name}</span>
                  <CheckIcon
                    className={cn(
                      "ml-auto h-4 w-4 shrink-0",
                      taskTemplate.journeyStageTemplate?._id.toString() ===
                        stageTemplate._id.toString()
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function Options({
  taskTemplate,
  isSubscribersActive,
  setIsSubscribersActive,
}: {
  taskTemplate: TaskTemplateData | undefined;
  isSubscribersActive: boolean;
  setIsSubscribersActive: (isActive: boolean) => void;
}) {
  const deleteTaskTemplateConfirmationMutation =
    useDeleteTaskTemplateConfirmation();
  const deleteTaskTemplateMutation = useDeleteTaskTemplate();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  // const updateTaskMutation = useUpdateTask();
  const taskTemplatePanelCtx = useTaskTemplatePanelContext();

  const [deletions, setDeletions] = useState<
    { _id: ObjectID; title: string }[]
  >([]);
  const [dependencies, setDependencies] = useState<
    { _id: ObjectID; title: string }[]
  >([]);

  useEffect(() => {
    if (deleteTaskTemplateConfirmationMutation.data) {
      setDeletions(deleteTaskTemplateConfirmationMutation.data.deleted);
      setDependencies(deleteTaskTemplateConfirmationMutation.data.dependencies);
      setIsConfirmationModalOpen(true);
    }
  }, [deleteTaskTemplateConfirmationMutation.data]);
  const [optionsOpen, setOptionsOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTaskTemplateFile = useUploadTaskTemplateFile();
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [isAddingMilestone, setIsAddingMilestone] = useState(false);
  // const [milestone, setMilestone] = useState<{
  //   _id: string;
  //   name: string;
  // } | null>(null);
  // const { data: milestones } = useGetMilestones();
  // const updateTaskTemplateMutation = useUpdateTaskTemplate();
  // const [milestoneOpen, setMilestoneOpen] = useState(false);
  // const { data: taskTemplates } = useGetTaskTemplates(
  //   new ObjectID(taskTemplate?.journeyTemplate._id)
  // );
  // const [milestoneOptions, setMilestoneOptions] = useState<{
  //   isLinkingWorkspaceMilestone: boolean;
  //   isApplyingToAllTasks: boolean;
  // }>({
  //   isLinkingWorkspaceMilestone: false,
  //   isApplyingToAllTasks: false,
  // });

  useEffect(() => {
    if (uploadTaskTemplateFile.isSuccess) {
      setIsOpen(false);
    }
  }, [uploadTaskTemplateFile.isSuccess]);

  const updateTaskTemplateMutation = useUpdateTaskTemplate();

  useEffect(() => {
    if (updateTaskTemplateMutation.isSuccess) {
      setIsAddingMilestone(false);
    }
  }, [updateTaskTemplateMutation.isSuccess]);

  useEffect(() => {
    setLabel("");
    setDescription("");
    setFile(undefined);
  }, [isOpen]);

  // const snackbarCtx = useSnackBar();

  const duplicateTaskTemplateMutation = useDuplicateTaskTemplate();
  // const usedMilestones = useMemo(() => {
  //   return (
  //     taskTemplates
  //       ?.filter((t) => t.milestone?._id)
  //       .map((t) => t.milestone?._id.toString() as string) ?? []
  //   );
  // }, [taskTemplates]);

  const [isApplyingMilestoneToAllTasks, setIsApplyingMilestoneToAllTasks] =
    useState(false);
  return (
    <>
      <DropdownMenu
        open={optionsOpen}
        onOpenChange={(isOpen) => {
          setOptionsOpen(isOpen);
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button
            variant={"ghost"}
            className={clsx(
              "h-8 w-8 p-0 hover:bg-gray-200/60 group-hover:visible",
              !optionsOpen ? "bg-transparent" : "bg-gray-200/60"
            )}
          >
            <EllipsisHorizontalIcon className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-44" align="end">
          <DropdownMenuGroup>
            {!taskTemplate?.isMilestone && (
              <DropdownMenuItem
                onClick={(e) => {
                  // if (
                  //   taskTemplate?._id &&
                  //   taskTemplate?.journeyStageTemplate._id &&
                  //   taskTemplate?.journeyTemplate._id
                  // ) {
                  //   updateTaskTemplateMutation.mutate({
                  //     params: {
                  //       _id: taskTemplate?._id,
                  //       isMilestone: true,
                  //     },
                  //     journeyStageTemplateId:
                  //       taskTemplate?.journeyStageTemplate._id,
                  //     journeyTemplateId: taskTemplate?.journeyTemplate._id,
                  //     parentTaskTemplateId:
                  //       taskTemplate?.parentTaskTemplate?._id,
                  //   });
                  // }
                  setIsAddingMilestone(true);
                  e.stopPropagation();
                }}
              >
                <MilestoneIcon />
                <span className="ml-2 text-sm">Mark as Milestone</span>
              </DropdownMenuItem>
            )}
            {!isSubscribersActive && (
              <>
                <DropdownMenuItem
                  onClick={(e) => {
                    setIsSubscribersActive(true);
                    e.stopPropagation();
                  }}
                >
                  <UsersIcon className="h-3.5 w-3.5 shrink-0" />
                  <span className="ml-2 text-sm">Add Subscribers</span>
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuItem
              onClick={(e) => {
                setIsOpen(true);
                e.stopPropagation();
              }}
            >
              <PaperClipIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="ml-2 text-sm">Add Attachment</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                // setIsOpen(true);
                if (
                  taskTemplate?._id &&
                  taskTemplate?.journeyTemplate._id &&
                  taskTemplate?.journeyStageTemplate._id
                ) {
                  duplicateTaskTemplateMutation.mutate({
                    params: {
                      taskTemplateId: taskTemplate?._id?.toString(),
                    },
                    metadata: {
                      journeyTemplateId:
                        taskTemplate?.journeyTemplate._id.toString(),
                      journeyStageTemplateId:
                        taskTemplate?.journeyStageTemplate._id.toString(),
                    },
                  });
                }
                e.stopPropagation();
              }}
            >
              <DocumentDuplicateIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="ml-2 text-sm">Duplicate</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {/* <DropdownMenuItem
                  // className="py-1 focus:bg-gray-50 focus:text-red-600"
                  onClick={(e) => {
                    setIsReordering(true);
                  }}
                >
                  <ArrowsUpDownIcon className="h-3.5 w-3.5 shrink-0" />
                  <span className="ml-2 text-sm">Reorder</span>
                </DropdownMenuItem> */}
            <DropdownMenuItem
              // className="py-1 focus:bg-gray-50 focus:text-red-600"
              className="focus:bg-red-50 focus:text-red-600"
              onClick={(e) => {
                setIsConfirmationModalOpen(true);
                e.stopPropagation();
              }}
            >
              <TrashIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="ml-2 text-sm">Delete</span>
            </DropdownMenuItem>
            {/* <DropdownMenuItem
                      // className="py-1 focus:bg-gray-50 focus:text-red-600"
                      className="focus:bg-red-50 focus:text-red-600"
                      onClick={(e) => {
                        setIsDeleting(true);
                        e.stopPropagation();
                      }}
                    >
                      <TrashIcon className="h-3.5 w-3.5 shrink-0" />
                      <span className="ml-2 text-sm">Delete</span>
                    </DropdownMenuItem> */}
            {/* <DropdownMenuItem>Delete</DropdownMenuItem> */}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Attachment</DialogTitle>
            <DialogDescription>
              Upload a file up to 5MB in size
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1">
              <InputHeading heading="Name" />
              <TextInput
                autoFocus
                className="max-w-[300px]"
                value={label}
                onChange={(e) => {
                  setLabel(e.target.value);
                }}
              />
            </div>
            <div className="space-y-1">
              <InputHeading heading="File" />
              <div className="flex space-x-3">
                <Button
                  size="fit"
                  variant={"link"}
                  onClick={() => {
                    fileInputRef.current?.click();
                  }}
                  // disabled={uploadLogoMutation.isLoading}
                >
                  <>{`${!!file ? "Change" : "Add"}`} file</>
                </Button>
                {!!file ? (
                  <div className="text-sm text-gray-800">{file.name}</div>
                ) : (
                  <div className="text-sm text-gray-400">No file selected</div>
                )}
              </div>
            </div>
            <input
              id="file-upload"
              name="file-upload"
              type="file"
              accept="application/pdf, image/jpg, image/jpeg, image/png, image/svg, image/gif, .doc, .docx, .ppt, .pptx, .xls, .xlsx, .csv, .txt"
              className="sr-only"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={(e) => {
                const selectedFile = e.target.files?.[0];
                if (selectedFile) {
                  if (
                    !/^[a-zA-Z0-9_ -]+\.[a-zA-Z0-9]+$/.test(selectedFile.name)
                  ) {
                    alert(
                      "The file name must consist solely of letters, numbers, underscores, hyphens, and a single dot. Please ensure it adheres to this format."
                    );
                  } else if (selectedFile.size > 5 * 1024 * 1024) {
                    alert("File size should not exceed 5MB.");
                  } else {
                    setFile(selectedFile);
                  }
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button
              disabled={!label || !file || uploadTaskTemplateFile.isLoading}
              onClick={() => {
                if (file && taskTemplate?._id) {
                  uploadTaskTemplateFile.mutate({
                    description,
                    taskTemplateId: taskTemplate._id.toString(),
                    file,
                    label,
                  });
                }
              }}
            >
              {uploadTaskTemplateFile.isLoading ? "Uploading.." : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isConfirmationModalOpen}
        onOpenChange={(open) => {
          setIsConfirmationModalOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Confirmation</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-3 text-sm text-gray-600">
            <div>
              Are you sure you want to delete task template{" "}
              <span className="font-medium text-gray-900">
                {taskTemplate?.title}
              </span>
              ?
            </div>
            {deletions.length > 0 && (
              <div className="space-y-1">
                <div>
                  <span>The following tasks will also be deleted -</span>
                </div>
                <ul className="list-disc space-y-1 pl-6">
                  {deletions.map((d) => (
                    <li key={d._id.toString()}>{d.title}</li>
                  ))}
                </ul>
              </div>
            )}
            {dependencies.length > 0 && (
              <div className="space-y-1">
                <div>
                  <span>The following tasks that used to be dependent on </span>
                  <span className="font-medium text-gray-900">
                    {taskTemplate?.title}
                  </span>{" "}
                  <span>will now be dependent on </span>
                  <span className="font-medium text-gray-900">
                    {"Project Start -"}
                  </span>
                </div>
                <ul className="list-disc space-y-1 pl-6">
                  {dependencies.map((d) => (
                    <li key={d._id.toString()}>{d.title}</li>
                  ))}
                </ul>
              </div>
            )}
            <div></div>
          </div>
          <DialogFooter>
            <DialogClose asChild className="">
              <div className="flex justify-end space-x-3">
                <Button
                  variant={"secondary"}
                  onClick={() => {
                    setIsConfirmationModalOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant={"destructive"}
                  onClick={() => {
                    if (taskTemplate?._id) {
                      deleteTaskTemplateMutation.mutate({
                        params: {
                          _id: taskTemplate?._id,
                          force: true,
                        },
                        journeyStageTemplateId:
                          taskTemplate.journeyStageTemplate._id,
                        journeyTemplateId: taskTemplate.journeyTemplate._id,
                        parentTaskTemplateId:
                          taskTemplate.parentTaskTemplate?._id,
                      });
                      taskTemplatePanelCtx.dispatch({
                        type: "TASK_TEMPLATE_PANEL_UPDATE",
                        taskTemplatePanel: {
                          id: undefined,
                          isOpen: false,
                        },
                      });
                    }
                    setIsConfirmationModalOpen(false);
                  }}
                >
                  Delete
                </Button>
              </div>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isAddingMilestone}
        onOpenChange={(open) => {
          setIsAddingMilestone(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as Milestone</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-8">
              {/* <div className="space-y-2">
                <div className="flex items-center justify-between gap-8">
                  <div className="space-y-1">
                    <InputHeading heading="Link to a Workspace Milestone" />
                    <div className="text-sm font-light text-gray-500">
                      Linking a milestone to a workspace milestone will enable
                      richer insights at the workspace level.
                    </div>
                  </div>
                  <Switch
                    checked={milestoneOptions.isLinkingWorkspaceMilestone}
                    onCheckedChange={(checked) => {
                      setMilestoneOptions({
                        ...milestoneOptions,
                        isLinkingWorkspaceMilestone: !!checked,
                      });
                    }}
                  />
                </div>
                {milestoneOptions.isLinkingWorkspaceMilestone && (
                  <ReusableMilestoneCombobox
                    milestone={milestone}
                    milestones={milestones ?? []}
                    setMilestone={(milestone) => {
                      setMilestone(milestone);
                    }}
                    usedMilestones={usedMilestones}
                  />
                )}
              </div> */}
              <div className="flex items-center justify-between gap-8">
                <div className="space-y-1">
                  <InputHeading heading="Apply to Previously Created Tasks" />
                  <div className="text-sm font-light text-gray-500">
                    Mark tasks previously created from this task template as
                    milestones as well.
                  </div>
                </div>
                <Switch
                  checked={isApplyingMilestoneToAllTasks}
                  onCheckedChange={(checked) => {
                    setIsApplyingMilestoneToAllTasks(!!checked);
                  }}
                />
              </div>
            </div>
            {/* <div className="space-y-1">
              <InputHeading heading="Apply Retroactively" />
              <div className="flex space-x-2">
                <div className="pt-0.5">
                  <Checkbox
                    checked={retroactivelyApplyMilestone}
                    onCheckedChange={(checked) => {
                      setRetroactivelyApplyMilestone(!!checked);
                    }}
                  />
                </div>
                <div className="text-xs font-light text-gray-600">
                  Apply milestone to tasks previously created from this task
                  template in currently active projects
                </div>
              </div>
            </div> */}
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              variant={"secondary"}
              onClick={() => {
                setIsAddingMilestone(false);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={updateTaskTemplateMutation.isLoading}
              onClick={() => {
                if (taskTemplate) {
                  // if (usedMilestones?.includes(milestone?._id)) {
                  //   snackbarCtx.showSnackbar("Milestone already used", "error");
                  //   return;
                  // }
                  updateTaskTemplateMutation.mutate({
                    journeyTemplateId: taskTemplate.journeyTemplate._id,
                    journeyStageTemplateId:
                      taskTemplate.journeyStageTemplate._id,
                    params: {
                      _id: taskTemplate._id,
                      milestone: {
                        isMilestone: true,
                        applyToAllTasks: isApplyingMilestoneToAllTasks,
                      },
                    },
                  });
                }
              }}
            >
              {updateTaskTemplateMutation.isLoading
                ? "Marking as Milestone..."
                : "Mark as Milestone"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
    // <div>
    //   <Menu
    //     side="right"
    //     items={[
    //       {
    //         text: "Delete",
    //         onClick: () => {
    //           if (taskTemplate?._id) {
    //             deleteTaskTemplateConfirmationMutation.mutate({
    //               params: {
    //                 _id: taskTemplate?._id,
    //                 force: false,
    //               },
    //               journeyStageTemplateId: taskTemplate.journeyStageTemplate._id,
    //               journeyTemplateId: taskTemplate.journeyTemplate._id,
    //             });
    //           }
    //         },
    //         icon: TrashIcon,
    //         disabled: !taskTemplate,
    //       },
    //     ]}
    //   >
    //     <div className="group inline-flex h-[30px] w-[30px] items-center justify-center rounded-md border border-gray-300 hover:border-gray-400 hover:bg-gray-50">
    //       <EllipsisVerticalIcon className="h-5 w-5 text-gray-400" />
    //     </div>
    //   </Menu>

    // </div>
  );
}

function RoleCombobox({ taskTemplate }: { taskTemplate: TaskTemplateData }) {
  const { journeyTemplateId } = useParams();
  const [open, setOpen] = useState(false);
  const { journeyTemplate } = useGetJourneyTemplate(String(journeyTemplateId));
  const updateTaskTemplateMutation = useUpdateTaskTemplate();
  const { data: roles } = useGetRoles();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          role="combobox"
          aria-expanded={open}
          className="max-w-[200px] space-x-2 truncate"
        >
          <UserIcon className="h-4 w-4 shrink-0" />
          {taskTemplate.tasker && (
            <div
              className={clsx(
                "h-2 w-2 shrink-0 rounded-full bg-gray-300",
                taskTemplate.tasker?.type === "internal"
                  ? "bg-gray-300"
                  : "bg-slate-700"
              )}
            />
          )}
          {taskTemplate.tasker?.role ? (
            <div className="truncate">{taskTemplate.tasker.role.name}</div>
          ) : (
            <div className="flex space-x-1 text-gray-400/80">
              Role
              <span className="top-0 ml-0.5 text-primary-main">*</span>
            </div>
          )}
          <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search assignee..." className="h-9" />
          <CommandList>
            <CommandEmpty>No role found.</CommandEmpty>
            <CommandGroup>
              {roles
                ?.filter((role) => role.type === "external")
                .map((role) => (
                  <CommandItem
                    key={role.name}
                    value={role.name}
                    onSelect={() => {
                      let params: Partial<TaskTemplateUpdateRequest> = {
                        _id: taskTemplate._id,
                        taskerRole: {
                          _id: new ObjectID(role._id),
                        },
                        taskerType: "external",
                        isVisible: true,
                      };
                      updateTaskTemplateMutation.mutate({
                        journeyTemplateId: taskTemplate.journeyTemplate._id,
                        journeyStageTemplateId:
                          taskTemplate.journeyStageTemplate._id,
                        params,
                        parentTaskTemplateId:
                          taskTemplate.parentTaskTemplate?._id,
                      });
                      setOpen(false);
                    }}
                    className="gap-2"
                  >
                    <div className="flex items-center space-x-2">
                      <div
                        className={"h-2 w-2 shrink-0 rounded-full bg-slate-700"}
                      />
                      <span className="truncate">{role.name}</span>
                    </div>
                    <CheckIcon
                      className={cn(
                        "ml-auto h-4 w-4 shrink-0",
                        taskTemplate.tasker?.role?._id.toString() ===
                          role._id.toString() &&
                          taskTemplate.tasker?.type === "external"
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              {roles
                ?.filter((role) => role.type === "internal")
                .map((role) => (
                  <CommandItem
                    key={role.name}
                    value={role.name}
                    onSelect={() => {
                      let params: Partial<TaskTemplateUpdateRequest> = {
                        _id: taskTemplate._id,
                        taskerRole: {
                          _id: new ObjectID(role._id),
                        },
                        taskerType: "internal",
                      };
                      updateTaskTemplateMutation.mutate({
                        journeyTemplateId: taskTemplate.journeyTemplate._id,
                        journeyStageTemplateId:
                          taskTemplate.journeyStageTemplate._id,
                        params,
                        parentTaskTemplateId:
                          taskTemplate.parentTaskTemplate?._id,
                      });
                      setOpen(false);
                    }}
                    className="gap-2"
                  >
                    <div className="flex items-center space-x-2">
                      <div
                        className={"h-2 w-2 shrink-0 rounded-full bg-gray-300"}
                      />
                      <span className="truncate">{role.name}</span>
                    </div>
                    <CheckIcon
                      className={cn(
                        "ml-auto h-4 w-4 shrink-0",
                        taskTemplate.tasker?.role?._id.toString() ===
                          role._id.toString() &&
                          taskTemplate.tasker?.type === "internal"
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
